/**
 * SmartTask V2 - Config Resolver
 * 
 * Merges configuration from 3 layers:
 * 1. Defaults (hardcoded sensible defaults)
 * 2. Template config (from operational_workflow_templates.config.smart_task)
 * 3. User feature settings (from module_access.custom_permissions)
 * 
 * Priority: defaults < template < user_features
 */

import type {
  SmartTaskConfig,
  OperationalRules,
  FeatureFlags,
  TerminologyConfig,
  QuickAction,
  DisplayMode,
  BeginActionConfig,
  BeginActionPanelAction,
  BeginActionProfile,
  BeginActionPickerMode,
  BeginActionSectionKey,
  BeginActionWorkflowSection,
  BeginActionWorkflowSectionAction,
  DetailProfile,
  DetailLayoutConfig,
  DetailLayoutTab,
  DetailTabKey,
  WorkItem,
  BeginActionStartAction,
  WorkflowHierarchyConfig,
  WorkflowHierarchyLevelConfig,
  WorkflowStatusesConfig,
  WorkflowStatusConfig,
  ChildEntryParentMode,
  ChildEntryType,
} from '../types';
import {
  collectTemplateActionSignals,
  getPickerModeForActionIntent,
  inferFeatureFlagsFromTemplateSignals,
  isCoreWorkflowLikeTemplateAction,
  resolveTemplateActionAlias,
} from '../utils/templateActionNormalization';
import { getEffectiveOperationsActionPermission } from '@/lib/operations/actionPermissions';
import { DEFAULT_FIELD_WORKER_SETTINGS } from '@/lib/operations/fieldWorkerDefaults';
import { resolveSmartTaskDisplayMode } from '@/lib/operations/smartTaskDisplayMode';
import {
  getSmartTaskColdStartNow,
  writeSmartTaskColdStartDuration,
  writeSmartTaskColdStartLog,
} from '../utils/coldStartDiagnostics';
import { hasAnyInspectionRules } from '../utils/vehicleInspectionStatus';
import { getWorkItemTypeRegistry } from '@/config/work-items/configLoader';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- SmartTask consumes highly dynamic template/user JSON config and this alias centralizes the legacy escape hatch.
type UnknownRecord = Record<string, any>;

// ============================================================================
// GENERIC PERMISSION KEY MAPPING
// ============================================================================

/**
 * Convert a snake_case permission key to camelCase.
 * e.g. 'smart_task_view_all_operations' → 'smartTaskViewAllOperations'
 */
function snakeToCamel(key: string): string {
  return key.replace(/_([a-z])/g, (_, char: string) => char.toUpperCase());
}

/**
 * Known overrides where the camelCase FeatureFlags property name does not
 * follow the simple snake_to_camel convention. Only entries where the
 * result differs from `snakeToCamel(key)` need to be listed here.
 */
const FEATURE_KEY_OVERRIDES: Record<string, string> = {
  // Short names (drop prefix)
  smart_task_view_all_operations: 'viewAllOperations',
  smart_task_child_entries: 'childEntries',
  smart_task_routes: 'routes',
  smart_task_appointments: 'appointments',
  smart_task_daily_forms: 'dailyForms',
  smart_task_job_metrics: 'jobMetrics',
  smart_task_toll_recording: 'tollRecording',
  smart_task_fuel_recording: 'fuelRecording',
  smart_task_breakdown_recording: 'breakdownRecording',
  smart_task_maintenance_recording: 'maintenanceRecording',
  smart_task_site_visit_recording: 'siteVisitRecording',
  smart_task_assessment_recording: 'assessmentRecording',
  smart_task_supervision_recording: 'supervisionRecording',
  smart_task_training_recording: 'trainingRecording',
  smart_task_route_stop_recording: 'routeStopRecording',
  smart_task_purchase_recording: 'purchaseRecording',
  smart_task_proposal_creation: 'proposalCreation',
  smart_task_credit_note_creation: 'creditNoteCreation',
  // smart_task_quote_creation, smart_task_invoice_creation, smart_task_expense_creation
  // migrated to operations_action_permissions (Actions tab SSOT).

  // Drop "job" from the camelCase name
  can_mark_job_en_route: 'canMarkEnRoute',
  can_mark_job_arrived: 'canMarkArrived',

  // Drop "enabled" suffix
  time_tracking_enabled: 'timeTracking',

  // Drop "enable_" prefix
  enable_qr_scanning: 'qrScanning',
  enable_barcode_scanning: 'barcodeScanning',
  enable_voice_notes: 'voiceNotes',
  enable_offline_mode: 'offlineMode',
  enable_photo_capture: 'photoCapture',
  enable_signature_capture: 'signatureCapture',
  enable_checklist_forms: 'checklistForms',
  enable_location_tagging: 'locationTagging',
  enable_parts_lookup: 'partsLookup',
  enable_customer_history: 'customerHistory',
  enable_navigation: 'navigation',

  // Acronym case differences
  enable_pod: 'enablePOD',
  enable_bom_viewer: 'enableBOMViewer',
  enable_ncr_reporting: 'enableNCRReporting',
  enable_qc_inspection: 'enableQCInspection',

  // Compound name
  enable_smart_clock_integration: 'smartClock',
};

/**
 * Generic mapping: apply any snake_case permission key from the effective
 * permissions object to the features object using camelCase conversion.
 * Keys already explicitly set in the features object are NOT overwritten.
 *
 * This ensures that ALL defined permissions (including new ones added to
 * permissionDefinitions.ts) are available to SmartTask consumers via
 * config.features.<camelCaseKey>, without needing to update configResolver
 * for each new permission.
 */
function applyGenericFeatureMappings(
  features: Record<string, unknown>,
  userFeatures: UnknownRecord,
): void {
  for (const [snakeKey, value] of Object.entries(userFeatures)) {
    if (value === undefined || value === null) continue;

    const camelKey = FEATURE_KEY_OVERRIDES[snakeKey] ?? snakeToCamel(snakeKey);

    // Don't overwrite keys that were already explicitly set
    if (features[camelKey] !== undefined) continue;

    // Only set boolean values for feature flags — non-boolean values
    // (like operations_action_permissions objects) are handled separately
    if (typeof value === 'boolean') {
      features[camelKey] = value;
    }
  }
}

// ============================================================================
// DEFAULTS
// ============================================================================

const DEFAULT_TERMINOLOGY: TerminologyConfig = {
  job: 'Job',
  jobs: 'Jobs',
  task: 'Task',
  tasks: 'Tasks',
  appointment: 'Appointment',
  checkin: 'Check In',
  checkout: 'Check Out',
  travel: 'Travel',
  complete: 'Complete',
  workItem: 'Work Item',
  workItems: 'Work Items',
};

function normalizeGeofenceEnforcementMode(value: unknown): OperationalRules['geofenceEnforcementMode'] {
  if (value === 'warn' || value === 'block' || value === 'none') {
    return value;
  }

  if (value === true || value === 'true') {
    return 'warn';
  }

  return 'none';
}

function normalizeScheduledInspectionInterval(
  intervalValue: unknown,
  frequencyValue?: unknown,
): OperationalRules['scheduledInspectionInterval'] {
  if (
    intervalValue === 'day'
    || intervalValue === 'week'
    || intervalValue === 'fortnight'
    || intervalValue === 'month'
    || intervalValue === 'quarter'
    || intervalValue === 'none'
  ) {
    return intervalValue;
  }

  const normalizedFrequency =
    frequencyValue === 'day'
      || frequencyValue === 'week'
      || frequencyValue === 'fortnight'
      || frequencyValue === 'month'
      || frequencyValue === 'quarter'
      ? frequencyValue
      : null;

  if (intervalValue === true || intervalValue === 'true') {
    return normalizedFrequency || 'week';
  }

  if (intervalValue === false || intervalValue === 'false') {
    return 'none';
  }

  return normalizedFrequency || 'none';
}

const DEFAULT_RULES: OperationalRules = {
  gpsEnforcement: 'recommended',
  requireGPSForTravel: false,
  requireGeofenceForCheckin: false,
  geofenceRadiusMeters: 100,
  requireTravel: false,
  allowSkipTravel: true,
  requireVehicle: false,
  requireVehicleProximity: false,
  vehicleProximityRadiusMeters: 500,
  requireOdometerReading: false,
  requireOdometerPhoto: false,
  requireETAForTravel: false,
  requireDigitalLogbook: false,
  requireDailyPreTripInspection: false,
  requireTakeOnInspection: false,
  requireHandoverInspection: false,
  requireAdminPresentForInspection: false,
  requireRandomInspection: false,
  randomInspectionLeniencyDays: 0,
  allowInspectionRemindLater: false,
  scheduledInspectionInterval: 'none' as const,
  requireInspectionAiAnpr: true,
  isNonDriver: false,
  allowSkipVehicleRequirement: false,
  requireValidDriversLicense: false,
  requireFleetDriverDesignation: false,
  requirePhotoOnStart: false,
  requirePhotoOnComplete: false,
  requireSignatureOnComplete: false,
  requireArrivalPhoto: false,
  requireChecklistComplete: false,
  allowMultipleSignatures: true,
  requireClockInForSmartTask: false,
  autoPresentJobsOnClockIn: DEFAULT_FIELD_WORKER_SETTINGS.auto_present_jobs_on_clock_in,
  autoCheckoutJobsOnClockOut: DEFAULT_FIELD_WORKER_SETTINGS.auto_checkout_jobs_on_clock_out,
  enableGapAnalysis: false,
  enableDailyComplianceSummary: false,
  geofenceEnforcementMode: 'none',
  allowMultipleJobsWithoutCheckout: false,
  allowMultipleSimultaneous: false,
  allowAppointmentDetours: false,
  viewSiblingAppointments: false,
  editSiblingAppointments: false,
  viewParentJobFromAppointment: false,
  downloadAssignedAppointmentPhotos: false,
  downloadParentJobPhotosFromAppointment: false,
  enforceRouteOrder: false,
  generatePdfOnCompletion: false,
  generateInvoiceOnComplete: false,
  generateQuoteOnComplete: false,
  autoPopulateMaterials: false,
  autoPopulateExpenses: false,
  autoPopulateTimeEntries: true,
  requirePartsBeforeComplete: false,
  requireDocumentBeforeComplete: false,
  requireDataValidationComplete: true,
};

const DEFAULT_FEATURES: FeatureFlags = {
  travelTracking: true,
  childEntries: true,
  routes: false,
  appointments: true,
  dailyForms: false,
  smartClock: false,
  jobMetrics: true,
  tollRecording: true,
  fuelRecording: true,
  breakdownRecording: true,
  maintenanceRecording: true,
  siteVisitRecording: true,
  assessmentRecording: true,
  supervisionRecording: false,
  trainingRecording: false,
  routeStopRecording: true,
  purchaseRecording: true,
  procurementDocumentCapture: false,
  qrScanning: true,
  barcodeScanning: true,
  voiceNotes: false,
  offlineMode: true,
  photoCapture: true,
  signatureCapture: true,
  checklistForms: true,
  locationTagging: true,
  timeTracking: true,
  partsLookup: false,
  customerHistory: false,
  navigation: true,
  podCapture: false,
  drawingViewer: false,
  bomLookup: false,
  canCreateJobs: false,
  canViewInventory: true,
  viewAllJobs: false,
  viewAllOperations: false,
  viewJobFinancials: false,
  viewCustomerDetails: true,
  canMarkEnRoute: true,
  canMarkArrived: true,
  canCompleteJobs: true,

  // Manufacturing / QA (template-driven)
  enableNCRReporting: false,
  enableBOMViewer: false,
  enableDrawingViewer: false,
  enableQCInspection: false,
  enableStageManagement: false,
  enableMeasurements: false,
  enableComplianceIncidents: false,
  enableComplianceAudits: false,
  enableComplianceCertifications: false,
  enableComplianceInspections: false,
  enableComplianceHazards: false,

  // Logistics (template-driven)
  enablePackageScanning: false,
  enableFailedDelivery: false,
  enableReportIssue: false,
  enableWaybillGeneration: false,
  enableManifest: false,
  enablePOD: false,

  // Inventory / Asset Management (template-driven)
  enableStockCount: false,

  // Field Service (template-driven)
  enableTimeEntry: false,
  enableQuoteGeneration: false,
  enableFollowUpScheduling: false,
  enableSiteAssessment: false,

  // Financial Management (workflow-section finance actions)
  proposalCreation: true,
  quoteCreation: true,
  invoiceCreation: true,
  expenseCreation: true,
  creditNoteCreation: true,

  // Fleet (template-driven)
  enableVehicleChange: false,
  enableOdometerCapture: false,

  // Document Generation (template-driven)
  enableDocumentGeneration: false,
  enableServiceReports: false,

  // Data Validation / Compliance
  enableDataValidation: false,
};

const DEFAULT_QUICK_ACTIONS: QuickAction[] = [
  { key: 'clock_in', icon: 'Clock', color: '#3b82f6', label: 'Clock In/Out' },
  { key: 'start_travel', icon: 'Navigation', color: '#8b5cf6', label: 'Start Travel' },
  { key: 'arrive_site', icon: 'MapPin', color: '#10b981', label: 'Arrive On Site' },
  { key: 'take_photo', icon: 'Camera', color: '#f59e0b', label: 'Take Photo' },
  { key: 'get_signature', icon: 'PenTool', color: '#a855f7', label: 'Get Signature' },
  { key: 'complete_job', icon: 'CheckCircle', color: '#22c55e', label: 'Complete Job' },
];

// ============================================================================
// SOLUTION DEFAULTS — per-industry baseline config layer
// Applied between DEFAULT_RULES and templateConfig
// ============================================================================

const SOLUTION_DEFAULTS: Record<string, { display_mode: string; default_template: string; field_worker_features: UnknownRecord }> = {
  'field-services': { display_mode: 'appointment_centric', default_template: 'service-operations', field_worker_features: { smart_task_field_worker: true, smart_task_appointments: true, smart_task_routes: true, smart_task_daily_forms: true, can_mark_job_en_route: true, can_mark_job_arrived: true, can_complete_jobs: true, require_signature: true, require_photos_after_complete: true, time_tracking_enabled: true } },
  'logistics-transport': { display_mode: 'route_centric', default_template: 'logistics-operations', field_worker_features: {} },
  'waste-management-enterprise': { display_mode: 'route_centric', default_template: 'waste-collection-operations', field_worker_features: {} },
  'workplace-management': { display_mode: 'unified', default_template: 'facility-management', field_worker_features: {} },
  'healthcare-services': { display_mode: 'appointment_centric', default_template: 'healthcare-operations', field_worker_features: {} },
  'advanced-manufacturing': { display_mode: 'task_centric', default_template: 'advanced-manufacturing', field_worker_features: {} },
  'security-services': { display_mode: 'route_centric', default_template: 'security-patrol-operations', field_worker_features: {} },
  'cleaning-services': { display_mode: 'route_centric', default_template: 'cleaning-operations', field_worker_features: {} },
  'construction-contracting': { display_mode: 'task_centric', default_template: 'construction-operations', field_worker_features: {} },
  'agriculture-farming': { display_mode: 'task_centric', default_template: 'agriculture-operations', field_worker_features: {} },
  'forestry-operations': { display_mode: 'task_centric', default_template: 'forestry-operations', field_worker_features: {} },
  'automotive-services': { display_mode: 'appointment_centric', default_template: 'automotive-operations', field_worker_features: {} },
  'real-estate-management': { display_mode: 'appointment_centric', default_template: 'real-estate-operations', field_worker_features: {} },
  'hospitality-management': { display_mode: 'unified', default_template: 'hospitality-operations', field_worker_features: {} },
  'mining-operations': { display_mode: 'task_centric', default_template: 'mining-operations', field_worker_features: {} },
  'energy-utilities': { display_mode: 'task_centric', default_template: 'energy-operations', field_worker_features: {} },
  'telecommunications': { display_mode: 'appointment_centric', default_template: 'telecom-operations', field_worker_features: {} },
  'marine-maritime': { display_mode: 'task_centric', default_template: 'marine-operations', field_worker_features: {} },
  'education-training': { display_mode: 'appointment_centric', default_template: 'education-operations', field_worker_features: {} },
  'legal-services': { display_mode: 'task_centric', default_template: 'legal-operations', field_worker_features: {} },
  'events-entertainment': { display_mode: 'unified', default_template: 'events-operations', field_worker_features: {} },
  'gis-solutions': { display_mode: 'task_centric', default_template: 'gis-operations', field_worker_features: {} },
  'retail-operations': { display_mode: 'unified', default_template: 'retail-operations', field_worker_features: {} },
  'restaurant-food-service': { display_mode: 'unified', default_template: 'restaurant-operations', field_worker_features: {} },
  'qa-management': { display_mode: 'task_centric', default_template: 'qa-operations', field_worker_features: {} },
  'nonprofit-management': { display_mode: 'unified', default_template: 'nonprofit-operations', field_worker_features: {} },
  'enterprise-project-management': { display_mode: 'task_centric', default_template: 'project-operations', field_worker_features: {} },
  'project-management': { display_mode: 'task_centric', default_template: 'project-operations', field_worker_features: {} },
  'software-development': { display_mode: 'task_centric', default_template: 'software-operations', field_worker_features: {} },
};

export { SOLUTION_DEFAULTS };

const BEGIN_ACTION_SECTION_KEYS: BeginActionSectionKey[] = [
  'primary_actions',
  'vehicle_fleet',
  'resource_tools',
  'quick_access',
  'industry_actions',
];

const BEGIN_ACTION_PICKER_MODES: BeginActionPickerMode[] = [
  'all',
  'job',
  'appointment',
  'task',
  'route',
  'incident',
  'audit',
  'certification',
  'inspection',
  'hazard',
];

const DETAIL_PROFILE_KEYS: DetailProfile[] = [
  'job',
  'commercial',
  'incident',
  'compliance',
  'general',
];

const DETAIL_TAB_KEYS: DetailTabKey[] = [
  'details',
  'work-breakdown',
  'photos',
  'comments',
  'materials',
  'expenses',
  'documents',
];

const DETAIL_TAB_LABELS: Record<DetailTabKey, string> = {
  details: 'Details',
  'work-breakdown': 'Breakdown',
  photos: 'Photos',
  comments: 'Comments',
  materials: 'Billing',
  expenses: 'Expenses',
  documents: 'Docs',
};

const COMMERCIAL_DETAIL_ITEM_TYPES = new Set([
  'quote',
  'delivery',
  'delivery_note',
  'sales_order',
  'purchase_order',
  'invoice',
  'expense',
  'credit_note',
  'proforma',
  'grn',
]);

const INCIDENT_DETAIL_ITEM_TYPES = new Set([
  'incident',
  'ticket',
  'service_request',
  'fault',
  'complaint',
  'case',
]);

const COMPLIANCE_DETAIL_ITEM_TYPES = new Set([
  'inspection',
  'audit',
  'compliance',
  'maintenance',
  'asset_maintenance',
]);

const BEGIN_ACTION_PROFILES: BeginActionProfile[] = [
  'field_operations',
  'asset_inventory',
  'knowledge_worker',
  'compliance',
  'general',
  'custom',
];

const DEFAULT_BEGIN_ACTION_PANEL_ACTIONS: Record<string, BeginActionPanelAction> = {
  tools_equipment: {
    key: 'tools_equipment',
    label: 'Tools & Equipment',
    description: 'Check-out, return & manage',
    icon: 'Wrench',
    color: '#475569',
    action: 'tools_equipment',
    section: 'resource_tools',
  },
  smart_clock: {
    key: 'smart_clock',
    label: 'Clock In/Out',
    icon: 'Clock',
    color: '#2563eb',
    action: 'smart_clock',
    section: 'quick_access',
  },
  fleet_manager: {
    key: 'fleet_manager',
    label: 'Fleet Manager',
    icon: 'Car',
    color: '#7c3aed',
    action: 'fleet_manager',
    section: 'quick_access',
  },
  log_time: {
    key: 'log_time',
    label: 'Time Entry',
    icon: 'Clock',
    color: '#2563eb',
    action: 'log_time',
    section: 'quick_access',
    requiresActiveJob: true,
    requiresCheckedIn: true,
  },
  take_photo: {
    key: 'take_photo',
    label: 'Take Photo',
    icon: 'Camera',
    color: '#f59e0b',
    action: 'take_photo',
    section: 'quick_access',
    requiresActiveJob: true,
  },
  get_signature: {
    key: 'get_signature',
    label: 'Add Signature',
    icon: 'PenTool',
    color: '#a855f7',
    action: 'get_signature',
    section: 'quick_access',
    requiresActiveJob: true,
  },
  generate_quote: {
    key: 'generate_quote',
    label: 'Create Quote',
    icon: 'FileText',
    color: '#16a34a',
    action: 'generate_quote',
    section: 'quick_access',
    requiresActiveJob: true,
    requiresCheckedIn: true,
  },
  schedule_follow_up: {
    key: 'schedule_follow_up',
    label: 'Follow-Up',
    icon: 'CalendarClock',
    color: '#7c3aed',
    action: 'schedule_follow_up',
    section: 'quick_access',
    requiresActiveJob: true,
  },
  scan_qr: {
    key: 'scan_qr',
    label: 'Scan QR',
    icon: 'QrCode',
    color: '#0891b2',
    action: 'scan_qr',
    section: 'quick_access',
    requiresActiveJob: true,
  },
  visitor_arrival: {
    key: 'visitor_arrival',
    label: 'Visitor Check-In',
    icon: 'UserPlus',
    color: '#10b981',
    action: 'visitor_arrival',
    section: 'quick_access',
  },
  visitor_departure: {
    key: 'visitor_departure',
    label: 'Visitor Check-Out',
    icon: 'UserMinus',
    color: '#f59e0b',
    action: 'visitor_departure',
    section: 'quick_access',
  },
  log_ncr: {
    key: 'log_ncr',
    label: 'Log NCR',
    icon: 'FileWarning',
    color: '#dc2626',
    action: 'log_ncr',
    section: 'quick_access',
    requiresActiveJob: true,
  },
  take_measurement: {
    key: 'take_measurement',
    label: 'Measure',
    icon: 'Ruler',
    color: '#0891b2',
    action: 'take_measurement',
    section: 'quick_access',
    requiresActiveJob: true,
  },
  run_qc: {
    key: 'run_qc',
    label: 'Quality Check',
    icon: 'ClipboardCheck',
    color: '#0f766e',
    action: 'run_qc',
    section: 'quick_access',
    requiresActiveJob: true,
  },
  complete_stage: {
    key: 'complete_stage',
    label: 'Complete Stage',
    icon: 'Layers3',
    color: '#7c3aed',
    action: 'complete_stage',
    section: 'quick_access',
    requiresActiveJob: true,
  },
  scan_package: {
    key: 'scan_package',
    label: 'Scan Package',
    icon: 'PackageSearch',
    color: '#0f766e',
    action: 'scan_package',
    section: 'quick_access',
    requiresActiveJob: true,
  },
  report_issue: {
    key: 'report_issue',
    label: 'Report Issue',
    icon: 'MessageSquare',
    color: '#ea580c',
    action: 'report_issue',
    section: 'quick_access',
    requiresActiveJob: true,
  },
  failed_delivery: {
    key: 'failed_delivery',
    label: 'Failed Delivery',
    icon: 'XCircle',
    color: '#dc2626',
    action: 'failed_delivery',
    section: 'quick_access',
    requiresActiveJob: true,
    requiresCheckedIn: true,
  },
  skip_stop: {
    key: 'skip_stop',
    label: 'Skip Stop',
    icon: 'SkipForward',
    color: '#64748b',
    action: 'skip_stop',
    section: 'quick_access',
    requiresActiveJob: true,
    requiresCheckedIn: true,
  },
  view_drawing: {
    key: 'view_drawing',
    label: 'View Drawing',
    icon: 'FileText',
    color: '#2563eb',
    action: 'view_drawing',
    section: 'quick_access',
    requiresActiveJob: true,
  },
  view_bom: {
    key: 'view_bom',
    label: 'View BOM',
    icon: 'Layers',
    color: '#7c3aed',
    action: 'view_bom',
    section: 'quick_access',
    requiresActiveJob: true,
  },
  generate_document: {
    key: 'generate_document',
    label: 'Documents',
    icon: 'FileText',
    color: '#7c3aed',
    action: 'generate_document',
    section: 'quick_access',
    requiresActiveJob: true,
  },
  digital_logbook: {
    key: 'digital_logbook',
    label: 'Digital Logbook',
    description: 'Log travel legs such as home to office, appointment trips, and return-to-base runs.',
    icon: 'BookOpen',
    color: '#059669',
    action: 'digital_logbook',
    section: 'quick_access',
  },
  operational_form: {
    key: 'operational_form',
    label: 'Operational Form',
    icon: 'ClipboardCheck',
    color: '#0f766e',
    action: 'operational_form',
    section: 'quick_access',
    requiresActiveJob: true,
  },
};

const QUICK_ACTION_ALIASES: Record<string, Partial<QuickAction> & { action: string }> = {
  clock_in: {
    action: 'smart_clock',
    label: 'Clock In/Out',
    icon: 'Clock',
    color: '#2563eb',
  },
  scan_checkpoint: {
    action: 'scan_qr',
    label: 'Scan Checkpoint',
    icon: 'QrCode',
    color: '#2563eb',
  },
  report_incident: {
    action: 'report_issue',
    label: 'Report Incident',
    icon: 'AlertTriangle',
    color: '#ef4444',
  },
  panic_alert: {
    action: 'report_issue',
    label: 'Panic Alert',
    icon: 'AlertOctagon',
    color: '#dc2626',
  },
  my_vehicle: {
    action: 'vehicle_actions',
    label: 'My Vehicle',
    icon: 'Truck',
    color: '#3b82f6',
  },
  create_followup: {
    action: 'schedule_follow_up',
    label: 'Follow-Up',
    icon: 'CalendarClock',
    color: '#7c3aed',
  },
  raise_followup: {
    action: 'schedule_follow_up',
    label: 'Follow-Up',
    icon: 'CalendarClock',
    color: '#7c3aed',
  },
  schedule_follow_up: {
    action: 'schedule_follow_up',
    label: 'Follow-Up',
    icon: 'CalendarClock',
    color: '#7c3aed',
    requiresActiveJob: true,
  },
  raise_quote: {
    action: 'generate_quote',
    label: 'Create Quote',
    icon: 'FileText',
    color: '#16a34a',
    requiresActiveJob: true,
    requiresCheckedIn: true,
  },
  generate_quote: {
    action: 'generate_quote',
    label: 'Create Quote',
    icon: 'FileText',
    color: '#16a34a',
    requiresActiveJob: true,
    requiresCheckedIn: true,
  },
  raise_ncr: {
    action: 'log_ncr',
    label: 'Log NCR',
    icon: 'FileWarning',
    color: '#dc2626',
    requiresActiveJob: true,
  },
  quality_check: {
    action: 'run_qc',
    label: 'Quality Check',
    icon: 'ClipboardCheck',
    color: '#0f766e',
    requiresActiveJob: true,
  },
  log_ncr: {
    action: 'log_ncr',
    label: 'Log NCR',
    icon: 'FileWarning',
    color: '#dc2626',
    requiresActiveJob: true,
  },
  scan_job: {
    action: 'scan_qr',
    label: 'Scan Job',
    icon: 'QrCode',
    color: '#0891b2',
  },
  scan_component: {
    action: 'scan_qr',
    label: 'Scan Component',
    icon: 'QrCode',
    color: '#0891b2',
    requiresActiveJob: true,
  },
  record_measurement: {
    action: 'take_measurement',
    label: 'Measure',
    icon: 'Ruler',
    color: '#0891b2',
    requiresActiveJob: true,
  },
  log_reading: {
    action: 'take_measurement',
    label: 'Measure',
    icon: 'Ruler',
    color: '#0891b2',
    requiresActiveJob: true,
  },
  record_vitals: {
    action: 'take_measurement',
    label: 'Vitals',
    icon: 'Ruler',
    color: '#0891b2',
    requiresActiveJob: true,
  },
  take_photo: {
    action: 'take_photo',
    label: 'Take Photo',
    icon: 'Camera',
    color: '#f59e0b',
    requiresActiveJob: true,
  },
  add_signature: {
    action: 'get_signature',
    label: 'Add Signature',
    icon: 'PenTool',
    color: '#a855f7',
    requiresActiveJob: true,
  },
  get_signature: {
    action: 'get_signature',
    label: 'Add Signature',
    icon: 'PenTool',
    color: '#a855f7',
    requiresActiveJob: true,
  },
  view_drawing: {
    action: 'view_drawing',
    label: 'View Drawing',
    icon: 'FileText',
    color: '#2563eb',
    requiresActiveJob: true,
  },
  log_observation: {
    action: 'operational_form',
    label: 'Log Observation',
    icon: 'Eye',
    color: '#4f46e5',
    requiresActiveJob: true,
  },
  create_capa: {
    action: 'generate_document',
    label: 'Create CAPA',
    icon: 'ClipboardCheck',
    color: '#7c3aed',
    requiresActiveJob: true,
  },
};

const BEGIN_ACTION_RESOURCE_SIGNAL_KEYS = new Set([
  'equipment_hours',
  'log_maintenance',
  'log_reading',
  'record_materials',
  'record_measurement',
  'run_inspection',
  'scan_asset',
  'scan_component',
  'scan_vehicle',
]);

const BEGIN_ACTION_TOOLS_PANEL_SIGNAL_KEYS = new Set([
  'equipment_hours',
  'log_maintenance',
  'run_inspection',
  'scan_job',
  'scan_asset',
  'scan_component',
  'scan_vehicle',
]);

const MAX_TEMPLATE_BEGIN_ACTION_SHORTCUTS = 8;

function isDefined<T>(value: T | null | undefined): value is T {
  return value != null;
}

function normalizeStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function normalizeQuickActions(
  value: unknown,
  actionContext: {
    displayMode: DisplayMode;
    allowedItemTypes: string[];
    requireTravel: boolean;
  },
): QuickAction[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const normalizedActions = value.map<QuickAction | null>((entry) => {
    if (typeof entry === 'string') {
      const rawKey = entry.trim();
      if (!rawKey) return null;

      const alias = {
        ...(resolveTemplateActionAlias(rawKey, actionContext) || {}),
        ...(QUICK_ACTION_ALIASES[rawKey] || {}),
      };
      const normalizedAction: QuickAction = {
        key: rawKey,
        label: alias.label || formatActionLabel(rawKey),
        icon: alias.icon || 'Layers',
        color: alias.color || '#64748b',
        action: alias.action || rawKey,
        requiresActiveJob: alias.requiresActiveJob,
        requiresCheckedIn: alias.requiresCheckedIn,
        group: alias.group,
      };

      return normalizedAction;
    }

    if (!entry || typeof entry !== 'object') {
      return null;
    }

    const rawQuickAction = entry as UnknownRecord;
    const rawKey = typeof rawQuickAction.key === 'string' && rawQuickAction.key.trim().length > 0
      ? rawQuickAction.key.trim()
      : typeof rawQuickAction.action === 'string' && rawQuickAction.action.trim().length > 0
        ? rawQuickAction.action.trim()
        : '';

    if (!rawKey) {
      return null;
    }

    const rawAction = typeof rawQuickAction.action === 'string' && rawQuickAction.action.trim().length > 0
      ? rawQuickAction.action.trim()
      : rawKey;
    const alias = {
      ...(resolveTemplateActionAlias(rawAction, actionContext) || resolveTemplateActionAlias(rawKey, actionContext) || {}),
      ...(QUICK_ACTION_ALIASES[rawAction] || QUICK_ACTION_ALIASES[rawKey] || {}),
    };

    const normalizedAction: QuickAction = {
      ...rawQuickAction,
      key: rawKey,
      label: typeof rawQuickAction.label === 'string' && rawQuickAction.label.trim().length > 0
        ? rawQuickAction.label.trim()
        : alias.label || formatActionLabel(rawKey),
      icon: typeof rawQuickAction.icon === 'string' && rawQuickAction.icon.trim().length > 0
        ? rawQuickAction.icon.trim()
        : alias.icon || 'Layers',
      color: typeof rawQuickAction.color === 'string' && rawQuickAction.color.trim().length > 0
        ? rawQuickAction.color.trim()
        : alias.color || '#64748b',
      action: typeof rawQuickAction.action === 'string' && rawQuickAction.action.trim().length > 0
        ? alias.action || rawQuickAction.action.trim()
        : alias.action || rawKey,
      requiresActiveJob: typeof rawQuickAction.requiresActiveJob === 'boolean'
        ? rawQuickAction.requiresActiveJob
        : typeof rawQuickAction.requires_active_job === 'boolean'
          ? rawQuickAction.requires_active_job
          : alias.requiresActiveJob,
      requiresCheckedIn: typeof rawQuickAction.requiresCheckedIn === 'boolean'
        ? rawQuickAction.requiresCheckedIn
        : typeof rawQuickAction.requires_checked_in === 'boolean'
          ? rawQuickAction.requires_checked_in
          : alias.requiresCheckedIn,
      group: typeof rawQuickAction.group === 'string' && rawQuickAction.group.trim().length > 0
        ? rawQuickAction.group.trim()
        : alias.group,
    };

    return normalizedAction;
  });

  return normalizedActions.filter(isDefined);
}

function formatActionLabel(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeBeginActionSections(value: unknown): BeginActionSectionKey[] {
  return Array.from(
    new Set(
      normalizeStringArray(value).filter((section): section is BeginActionSectionKey => (
        BEGIN_ACTION_SECTION_KEYS as string[]
      ).includes(section))
    )
  );
}

function normalizeBeginActionPanelActions(value: unknown): BeginActionPanelAction[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const normalizedActions = value.map<BeginActionPanelAction | null>((entry) => {
    if (typeof entry === 'string') {
      const defaultAction = DEFAULT_BEGIN_ACTION_PANEL_ACTIONS[entry];
      const normalizedAction: BeginActionPanelAction = defaultAction
        ? { ...defaultAction }
        : {
          key: entry,
          label: formatActionLabel(entry),
          action: entry,
        };

      return normalizedAction;
    }

    if (!entry || typeof entry !== 'object') {
      return null;
    }

    const rawAction = entry as UnknownRecord;
    const actionKey = typeof rawAction.key === 'string' && rawAction.key.trim().length > 0
      ? rawAction.key.trim()
      : typeof rawAction.action === 'string' && rawAction.action.trim().length > 0
        ? rawAction.action.trim()
        : null;

    if (!actionKey) {
      return null;
    }

    const defaultAction = DEFAULT_BEGIN_ACTION_PANEL_ACTIONS[actionKey];
    const rawSection = typeof rawAction.section === 'string' ? rawAction.section.trim() : '';
    const section: BeginActionPanelAction['section'] = (rawSection === 'resource_tools' || rawSection === 'quick_access')
      ? rawSection
      : defaultAction?.section;

    const normalizedAction: BeginActionPanelAction = {
      ...defaultAction,
      ...rawAction,
      key: actionKey,
      action: typeof rawAction.action === 'string' && rawAction.action.trim().length > 0
        ? rawAction.action.trim()
        : defaultAction?.action || actionKey,
      label: typeof rawAction.label === 'string' && rawAction.label.trim().length > 0
        ? rawAction.label.trim()
        : defaultAction?.label || formatActionLabel(actionKey),
      description: typeof rawAction.description === 'string'
        ? rawAction.description
        : defaultAction?.description,
      icon: typeof rawAction.icon === 'string' && rawAction.icon.trim().length > 0
        ? rawAction.icon.trim()
        : defaultAction?.icon,
      color: typeof rawAction.color === 'string' && rawAction.color.trim().length > 0
        ? rawAction.color.trim()
        : defaultAction?.color,
      section,
      requiresVehicle: typeof rawAction.requiresVehicle === 'boolean'
        ? rawAction.requiresVehicle
        : defaultAction?.requiresVehicle,
      requiresActiveJob: typeof rawAction.requiresActiveJob === 'boolean'
        ? rawAction.requiresActiveJob
        : typeof rawAction.requires_active_job === 'boolean'
          ? rawAction.requires_active_job
          : defaultAction?.requiresActiveJob,
      requiresCheckedIn: typeof rawAction.requiresCheckedIn === 'boolean'
        ? rawAction.requiresCheckedIn
        : typeof rawAction.requires_checked_in === 'boolean'
          ? rawAction.requires_checked_in
          : defaultAction?.requiresCheckedIn,
    };

    return normalizedAction;
  });

  return normalizedActions.filter(isDefined);
}

function dedupeBeginActionPanelActions(actions: BeginActionPanelAction[]): BeginActionPanelAction[] {
  return Array.from(new Map(actions.map((action) => [action.action || action.key, action])).values());
}

function normalizeWorkflowToken(value: unknown): string {
  return typeof value === 'string'
    ? value.trim().toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_')
    : '';
}

function buildWorkflowLevelKey(itemType?: string | null, itemSubtype?: string | null): string {
  const normalizedType = normalizeWorkflowToken(itemType);
  const normalizedSubtype = normalizeWorkflowToken(itemSubtype);

  if (!normalizedType) return '';
  if (!normalizedSubtype) return normalizedType;
  return `${normalizedType}:${normalizedSubtype}`;
}

function normalizeWorkflowHierarchy(value: unknown): WorkflowHierarchyConfig {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { levels: [], relationships: [] };
  }

  const rawHierarchy = value as UnknownRecord;

  const levels = Array.isArray(rawHierarchy.levels)
    ? rawHierarchy.levels
      .map<WorkflowHierarchyLevelConfig | null>((entry) => {
        if (!entry || typeof entry !== 'object') {
          return null;
        }

        const rawLevel = entry as UnknownRecord;
        const type = typeof rawLevel.type === 'string' ? rawLevel.type.trim() : '';
        if (!type) {
          return null;
        }

        const subtype = typeof rawLevel.subtype === 'string' && rawLevel.subtype.trim().length > 0
          ? rawLevel.subtype.trim()
          : undefined;

        return {
          type,
          subtype,
          label: typeof rawLevel.label === 'string' && rawLevel.label.trim().length > 0
            ? rawLevel.label.trim()
            : formatActionLabel(subtype || type),
          prefix: typeof rawLevel.prefix === 'string' && rawLevel.prefix.trim().length > 0
            ? rawLevel.prefix.trim()
            : undefined,
          icon: typeof rawLevel.icon === 'string' && rawLevel.icon.trim().length > 0
            ? rawLevel.icon.trim()
            : undefined,
        } satisfies WorkflowHierarchyLevelConfig;
      })
      .filter((level): level is WorkflowHierarchyLevelConfig => Boolean(level))
    : [];

  const relationships = Array.isArray(rawHierarchy.relationships)
    ? rawHierarchy.relationships
      .map<{ parent: string; child: string } | null>((entry) => {
        if (!entry || typeof entry !== 'object') {
          return null;
        }

        const rawRelationship = entry as UnknownRecord;
        const parent = typeof rawRelationship.parent === 'string' ? rawRelationship.parent.trim() : '';
        const child = typeof rawRelationship.child === 'string' ? rawRelationship.child.trim() : '';

        if (!parent || !child) {
          return null;
        }

        return { parent, child };
      })
      .filter((relationship): relationship is { parent: string; child: string } => Boolean(relationship))
    : [];

  return { levels, relationships };
}

function normalizeWorkflowStatuses(value: unknown): WorkflowStatusesConfig {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value as UnknownRecord).reduce<WorkflowStatusesConfig>((acc, [itemKey, rawStatuses]) => {
    if (!Array.isArray(rawStatuses)) {
      return acc;
    }

    const normalizedItemKey = normalizeWorkflowToken(itemKey).includes(':')
      ? itemKey.trim().toLowerCase()
      : normalizeWorkflowToken(itemKey);

    const statusList = rawStatuses
      .map<WorkflowStatusConfig | null>((entry) => {
        if (!entry || typeof entry !== 'object') {
          return null;
        }

        const rawStatus = entry as UnknownRecord;
        const key = typeof rawStatus.key === 'string' ? rawStatus.key.trim() : '';
        if (!key) {
          return null;
        }

        return {
          key,
          label: typeof rawStatus.label === 'string' && rawStatus.label.trim().length > 0
            ? rawStatus.label.trim()
            : formatActionLabel(key),
          color: typeof rawStatus.color === 'string' && rawStatus.color.trim().length > 0
            ? rawStatus.color.trim()
            : undefined,
          description: typeof rawStatus.description === 'string'
            ? rawStatus.description
            : undefined,
          is_initial: Boolean(rawStatus.is_initial),
          is_final: Boolean(rawStatus.is_final),
          mobile_actions: normalizeStringArray(rawStatus.mobile_actions),
          triggers: normalizeStringArray(rawStatus.triggers),
        } satisfies WorkflowStatusConfig;
      })
      .filter((status): status is WorkflowStatusConfig => Boolean(status));

    if (statusList.length > 0) {
      acc[normalizedItemKey] = mergeSSOTStatuses(normalizedItemKey, statusList);
    }

    return acc;
  }, {});
}

function mergeSSOTStatuses(itemKey: string, templateStatuses: WorkflowStatusConfig[]): WorkflowStatusConfig[] {
  try {
    const registry = getWorkItemTypeRegistry();
    if (!registry.isInitialized()) return templateStatuses;

    const ssotStatuses = registry.getSmartTaskVisibleStatuses(itemKey);
    if (ssotStatuses.length === 0) return templateStatuses;

    const ssotMapped: WorkflowStatusConfig[] = ssotStatuses.map((s) => ({
      key: s.key,
      label: s.label,
      color: s.color,
      description: s.description,
      is_initial: s.is_initial,
      is_final: s.is_terminal,
      mobile_actions: s.mobile_actions || [],
      triggers: s.triggers || [],
    }));

    for (const ssot of ssotMapped) {
      const tmpl = templateStatuses.find((t) => t.key === ssot.key);
      if (tmpl) {
        if (tmpl.mobile_actions?.length) ssot.mobile_actions = tmpl.mobile_actions;
        if (tmpl.triggers?.length) ssot.triggers = tmpl.triggers;
        ssot.description = ssot.description || tmpl.description;
      }
    }

    return ssotMapped;
  } catch {
    return templateStatuses;
  }
}

function normalizeBeginActionStartActions(value: unknown): BeginActionStartAction[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map<BeginActionStartAction | null>((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }

      const rawAction = entry as UnknownRecord;
      const itemType = typeof rawAction.item_type === 'string'
        ? rawAction.item_type.trim()
        : typeof rawAction.itemType === 'string'
          ? rawAction.itemType.trim()
          : '';

      if (!itemType) {
        return null;
      }

      const itemSubtype = typeof rawAction.item_subtype === 'string' && rawAction.item_subtype.trim().length > 0
        ? rawAction.item_subtype.trim()
        : typeof rawAction.itemSubtype === 'string' && rawAction.itemSubtype.trim().length > 0
          ? rawAction.itemSubtype.trim()
          : undefined;

      const label = typeof rawAction.label === 'string' && rawAction.label.trim().length > 0
        ? rawAction.label.trim()
        : formatActionLabel(itemSubtype || itemType);

      const displayItemType = typeof rawAction.display_item_type === 'string' && rawAction.display_item_type.trim().length > 0
        ? rawAction.display_item_type.trim()
        : typeof rawAction.displayItemType === 'string' && rawAction.displayItemType.trim().length > 0
          ? rawAction.displayItemType.trim()
          : undefined;

      const key = typeof rawAction.key === 'string' && rawAction.key.trim().length > 0
        ? rawAction.key.trim()
        : buildWorkflowLevelKey(itemType, itemSubtype);

      return {
        key,
        label,
        sublabel: typeof rawAction.sublabel === 'string'
          ? rawAction.sublabel
          : typeof rawAction.description === 'string'
            ? rawAction.description
            : undefined,
        icon: typeof rawAction.icon === 'string' && rawAction.icon.trim().length > 0
          ? rawAction.icon.trim()
          : undefined,
        color: typeof rawAction.color === 'string' && rawAction.color.trim().length > 0
          ? rawAction.color.trim()
          : undefined,
        mode: rawAction.mode === 'select_existing' ? 'select_existing' : 'create',
        displayItemType,
        itemType,
        itemSubtype,
        initialStatus: typeof rawAction.initial_status === 'string' && rawAction.initial_status.trim().length > 0
          ? rawAction.initial_status.trim()
          : typeof rawAction.initialStatus === 'string' && rawAction.initialStatus.trim().length > 0
            ? rawAction.initialStatus.trim()
            : undefined,
        formCategory: typeof rawAction.form_category === 'string' && rawAction.form_category.trim().length > 0
          ? rawAction.form_category.trim()
          : typeof rawAction.formCategory === 'string' && rawAction.formCategory.trim().length > 0
            ? rawAction.formCategory.trim()
            : undefined,
      } satisfies BeginActionStartAction;
    })
    .filter((action): action is BeginActionStartAction => Boolean(action));
}

function normalizeBeginActionPickerMode(value: unknown): BeginActionPickerMode | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalizedValue = value.trim().toLowerCase();
  return (BEGIN_ACTION_PICKER_MODES as string[]).includes(normalizedValue)
    ? normalizedValue as BeginActionPickerMode
    : undefined;
}

function inferBeginActionPickerModeFromAction(action: string | undefined): BeginActionPickerMode | undefined {
  if (!action) {
    return undefined;
  }

  return getPickerModeForActionIntent(action) || undefined;
}

const WORKFLOW_SECTION_CHILD_ENTRY_TYPES = new Set<ChildEntryType>([
  'toll',
  'fuel',
  'maintenance',
  'site_visit',
  'assessment',
  'supervision',
  'training',
  'route_stop',
  'purchase',
]);

function normalizeWorkflowSectionChildEntryType(value: unknown): ChildEntryType | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalizedValue = normalizeWorkflowToken(value);
  return WORKFLOW_SECTION_CHILD_ENTRY_TYPES.has(normalizedValue as ChildEntryType)
    ? normalizedValue as ChildEntryType
    : undefined;
}

function normalizeChildEntryParentMode(value: unknown): ChildEntryParentMode | undefined {
  return value === 'standalone' || value === 'current_work_item'
    ? value
    : undefined;
}

function normalizeBeginActionWorkflowSectionActions(value: unknown): BeginActionWorkflowSectionAction[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map<BeginActionWorkflowSectionAction | null>((entry) => {
      if (typeof entry === 'string') {
        const actionKey = entry.trim();
        if (!actionKey) {
          return null;
        }

        const resolvedAlias = resolveTemplateActionAlias(actionKey, {
          displayMode: 'unified',
          allowedItemTypes: [],
          requireTravel: false,
        });

        return {
          key: actionKey,
          label: resolvedAlias?.label || formatActionLabel(actionKey),
          icon: resolvedAlias?.icon,
          color: resolvedAlias?.color,
          action: resolvedAlias?.action || actionKey,
          mode: resolvedAlias?.action ? 'select_existing' : 'action',
          pickerMode: inferBeginActionPickerModeFromAction(resolvedAlias?.action),
        } satisfies BeginActionWorkflowSectionAction;
      }

      if (!entry || typeof entry !== 'object') {
        return null;
      }

      const rawAction = entry as UnknownRecord;
      const key = typeof rawAction.key === 'string' && rawAction.key.trim().length > 0
        ? rawAction.key.trim()
        : typeof rawAction.action === 'string' && rawAction.action.trim().length > 0
          ? rawAction.action.trim()
          : typeof rawAction.label === 'string' && rawAction.label.trim().length > 0
            ? normalizeWorkflowToken(rawAction.label)
            : '';

      if (!key) {
        return null;
      }

      const action = typeof rawAction.action === 'string' && rawAction.action.trim().length > 0
        ? rawAction.action.trim()
        : undefined;
      const pickerMode = normalizeBeginActionPickerMode(rawAction.picker_mode ?? rawAction.pickerMode)
        || inferBeginActionPickerModeFromAction(action);
      const childEntryType = normalizeWorkflowSectionChildEntryType(
        rawAction.child_entry_type ?? rawAction.childEntryType
      );
      const itemTypes = normalizeStringArray(rawAction.item_types ?? rawAction.itemTypes)
        .map((itemType) => normalizeWorkflowToken(itemType))
        .filter(Boolean);
      const mode = rawAction.mode === 'create' || rawAction.mode === 'select_existing' || rawAction.mode === 'action'
        ? rawAction.mode
        : childEntryType
          ? 'action'
          : (pickerMode || itemTypes.length > 0)
            ? 'select_existing'
            : 'action';

      return {
        key,
        label: typeof rawAction.label === 'string' && rawAction.label.trim().length > 0
          ? rawAction.label.trim()
          : formatActionLabel(key),
        description: typeof rawAction.description === 'string' && rawAction.description.trim().length > 0
          ? rawAction.description.trim()
          : typeof rawAction.sublabel === 'string' && rawAction.sublabel.trim().length > 0
            ? rawAction.sublabel.trim()
            : undefined,
        icon: typeof rawAction.icon === 'string' && rawAction.icon.trim().length > 0
          ? rawAction.icon.trim()
          : undefined,
        color: typeof rawAction.color === 'string' && rawAction.color.trim().length > 0
          ? rawAction.color.trim()
          : undefined,
        action,
        mode,
        pickerMode,
        itemTypes: itemTypes.length > 0 ? itemTypes : undefined,
        startLabel: typeof rawAction.start_label === 'string' && rawAction.start_label.trim().length > 0
          ? rawAction.start_label.trim()
          : typeof rawAction.startLabel === 'string' && rawAction.startLabel.trim().length > 0
            ? rawAction.startLabel.trim()
            : undefined,
        emptyMessage: typeof rawAction.empty_message === 'string' && rawAction.empty_message.trim().length > 0
          ? rawAction.empty_message.trim()
          : typeof rawAction.emptyMessage === 'string' && rawAction.emptyMessage.trim().length > 0
            ? rawAction.emptyMessage.trim()
            : undefined,
        childEntryType,
        childEntryParentMode: normalizeChildEntryParentMode(
          rawAction.child_entry_parent_mode ?? rawAction.childEntryParentMode
        ),
        requiresVehicle: typeof rawAction.requiresVehicle === 'boolean'
          ? rawAction.requiresVehicle
          : typeof rawAction.requires_vehicle === 'boolean'
            ? rawAction.requires_vehicle
            : undefined,
        requiresActiveJob: typeof rawAction.requiresActiveJob === 'boolean'
          ? rawAction.requiresActiveJob
          : typeof rawAction.requires_active_job === 'boolean'
            ? rawAction.requires_active_job
            : undefined,
        requiresCheckedIn: typeof rawAction.requiresCheckedIn === 'boolean'
          ? rawAction.requiresCheckedIn
          : typeof rawAction.requires_checked_in === 'boolean'
            ? rawAction.requires_checked_in
            : undefined,
        requiresPermission: typeof rawAction.requires_permission === 'string' && rawAction.requires_permission.trim().length > 0
          ? rawAction.requires_permission.trim()
          : typeof rawAction.requiresPermission === 'string' && rawAction.requiresPermission.trim().length > 0
            ? rawAction.requiresPermission.trim()
            : undefined,
      } satisfies BeginActionWorkflowSectionAction;
    })
    .filter((action): action is BeginActionWorkflowSectionAction => Boolean(action));
}

function getWorkflowSectionActionIdentity(action: BeginActionWorkflowSectionAction): string {
  const normalizedItemTypes = (action.itemTypes || []).map((itemType) => normalizeWorkflowToken(itemType)).sort();

  if (action.childEntryType) {
    return `child-entry:${action.childEntryType}:${action.childEntryParentMode || 'current_work_item'}`;
  }

  if (action.action && action.action.trim().length > 0) {
    return `action:${action.action.trim().toLowerCase()}:${normalizedItemTypes.join(',')}`;
  }

  if (action.pickerMode) {
    return `picker:${action.pickerMode}:${normalizedItemTypes.join(',')}`;
  }

  return `key:${action.key.trim().toLowerCase()}`;
}

function dedupeBeginActionWorkflowSectionActions(actions: BeginActionWorkflowSectionAction[]): BeginActionWorkflowSectionAction[] {
  const dedupedActions = new Map<string, BeginActionWorkflowSectionAction>();

  for (const action of actions) {
    const identity = getWorkflowSectionActionIdentity(action);
    if (!dedupedActions.has(identity)) {
      dedupedActions.set(identity, action);
    }
  }

  return Array.from(dedupedActions.values());
}

function normalizeBeginActionWorkflowSections(value: unknown): BeginActionWorkflowSection[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const normalizedSections = value
    .map<BeginActionWorkflowSection | null>((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }

      const rawSection = entry as UnknownRecord;
      const sectionKey = typeof rawSection.key === 'string' && rawSection.key.trim().length > 0
        ? rawSection.key.trim()
        : typeof rawSection.title === 'string' && rawSection.title.trim().length > 0
          ? normalizeWorkflowToken(rawSection.title)
          : '';

      if (!sectionKey) {
        return null;
      }

      return {
        key: sectionKey,
        title: typeof rawSection.title === 'string' && rawSection.title.trim().length > 0
          ? rawSection.title.trim()
          : formatActionLabel(sectionKey),
        description: typeof rawSection.description === 'string' && rawSection.description.trim().length > 0
          ? rawSection.description.trim()
          : undefined,
        icon: typeof rawSection.icon === 'string' && rawSection.icon.trim().length > 0
          ? rawSection.icon.trim()
          : undefined,
        color: typeof rawSection.color === 'string' && rawSection.color.trim().length > 0
          ? rawSection.color.trim()
          : undefined,
        templateKey: typeof rawSection.template_key === 'string' && rawSection.template_key.trim().length > 0
          ? rawSection.template_key.trim()
          : typeof rawSection.templateKey === 'string' && rawSection.templateKey.trim().length > 0
            ? rawSection.templateKey.trim()
            : undefined,
        templateName: typeof rawSection.template_name === 'string' && rawSection.template_name.trim().length > 0
          ? rawSection.template_name.trim()
          : typeof rawSection.templateName === 'string' && rawSection.templateName.trim().length > 0
            ? rawSection.templateName.trim()
            : undefined,
        actions: dedupeBeginActionWorkflowSectionActions(
          normalizeBeginActionWorkflowSectionActions(rawSection.actions)
        ),
      } satisfies BeginActionWorkflowSection;
    })
    .filter((section): section is BeginActionWorkflowSection => Boolean(section));

  return Array.from(new Map(normalizedSections.map((section) => [section.key, section])).values());
}

function dedupeBeginActionStartActions(actions: BeginActionStartAction[]): BeginActionStartAction[] {
  return Array.from(new Map(actions.map((action) => [action.key, action])).values());
}

const PREFERRED_START_STATUS_KEYS = ['in_progress', 'in-progress', 'working', 'started', 'checked_in'];

function resolveStartActionInitialStatus(
  itemType: string,
  itemSubtype: string | undefined,
  workflowStatuses: WorkflowStatusesConfig,
): string | undefined {
  // SSOT primary: try configLoader for initial status first
  try {
    const registry = getWorkItemTypeRegistry();
    if (registry.isInitialized()) {
      const ssotStatuses = registry.getStatusesForItemType(itemType);
      if (ssotStatuses.length > 0) {
        const activeStatus = ssotStatuses.find((s) => PREFERRED_START_STATUS_KEYS.includes(normalizeWorkflowToken(s.key)));
        if (activeStatus?.key) return activeStatus.key;
        const initialStatus = ssotStatuses.find((s) => s.is_initial);
        if (initialStatus?.key) return initialStatus.key;
      }
    }
  } catch { /* fallback below */ }

  // Template config fallback
  const candidateKeys = [
    buildWorkflowLevelKey(itemType, itemSubtype),
    normalizeWorkflowToken(itemType),
  ].filter(Boolean);

  for (const candidateKey of candidateKeys) {
    const statuses = workflowStatuses[candidateKey];
    if (!Array.isArray(statuses) || statuses.length === 0) {
      continue;
    }

    const activeStatus = statuses.find((status) => PREFERRED_START_STATUS_KEYS.includes(normalizeWorkflowToken(status.key)));
    if (activeStatus?.key) {
      return activeStatus.key;
    }

    const initialStatus = statuses.find((status) => status.is_initial);
    if (initialStatus?.key) {
      return initialStatus.key;
    }

    if (statuses[0]?.key) {
      return statuses[0].key;
    }
  }

  return undefined;
}

const COMPLIANCE_BEGIN_ACTION_SPECS = [
  {
    key: 'incident',
    label: 'Incident',
    icon: 'AlertTriangle',
    color: '#e11d48',
    preferredHierarchyKeys: ['ticket:incident_report', 'incident'],
    fallbackItemType: 'incident',
  },
  {
    key: 'audit',
    label: 'Audit',
    icon: 'ClipboardCheck',
    color: '#059669',
    preferredHierarchyKeys: ['ticket:audit', 'audit'],
    fallbackItemType: 'audit',
  },
  {
    key: 'certification',
    label: 'Certificate',
    icon: 'Shield',
    color: '#4f46e5',
    preferredHierarchyKeys: ['certification', 'certificate'],
    fallbackItemType: 'certification',
  },
  {
    key: 'inspection',
    label: 'Inspection',
    icon: 'CheckSquare',
    color: '#d97706',
    preferredHierarchyKeys: ['ticket:safety_inspection', 'inspection'],
    fallbackItemType: 'inspection',
  },
  {
    key: 'hazard',
    label: 'Hazard',
    icon: 'FileWarning',
    color: '#ea580c',
    preferredHierarchyKeys: ['ticket:risk_assessment', 'hazard'],
    fallbackItemType: 'hazard',
  },
  {
    key: 'appointment',
    label: 'Appointment',
    icon: 'Calendar',
    color: '#7c3aed',
    preferredHierarchyKeys: ['appointment'],
    fallbackItemType: 'appointment',
  },
] as const;

const ADVANCED_MANUFACTURING_START_ACTIONS: BeginActionStartAction[] = [
  {
    key: 'production_orders',
    label: 'Start Work Order',
    sublabel: 'Select a production order or work order to begin execution',
    icon: 'Factory',
    color: '#ea580c',
    mode: 'select_existing',
    itemType: 'production_order',
  },
  {
    key: 'manufacturing_stages',
    label: 'Start Production Stage',
    sublabel: 'Select the next assigned stage to begin or resume',
    icon: 'GitBranch',
    color: '#7c3aed',
    mode: 'select_existing',
    itemType: 'manufacturing_stage',
  },
  {
    key: 'quality_checks',
    label: 'Start QC Check',
    sublabel: 'Open a quality-check step for the current production flow',
    icon: 'ClipboardCheck',
    color: '#059669',
    mode: 'select_existing',
    itemType: 'quality_check',
  },
  {
    key: 'inspections',
    label: 'Start Inspection',
    sublabel: 'Open an inspection linked to the work order or stage',
    icon: 'CheckSquare',
    color: '#d97706',
    mode: 'select_existing',
    itemType: 'inspection',
  },
  {
    key: 'ncrs',
    label: 'Start NCR Flow',
    sublabel: 'Pick an NCR or exception item that needs attention',
    icon: 'FileWarning',
    color: '#dc2626',
    mode: 'select_existing',
    itemType: 'ncr',
  },
];

type CanCreateItemType = (itemType: string) => boolean;

function canCreateAnyWorkflowItemType(
  canCreateItemType: CanCreateItemType,
  ...itemTypes: Array<string | undefined>
): boolean {
  return itemTypes.some((itemType) => Boolean(itemType) && canCreateItemType(itemType!));
}

function inferComplianceStartActions(
  allowedItemTypes: string[],
  workflowHierarchy: WorkflowHierarchyConfig,
  workflowStatuses: WorkflowStatusesConfig,
  canCreateItemType: CanCreateItemType,
): BeginActionStartAction[] {
  const allowedTypeSet = new Set(allowedItemTypes.map((itemType) => normalizeWorkflowToken(itemType)));
  const levelsByKey = new Map(
    workflowHierarchy.levels.map((level) => [buildWorkflowLevelKey(level.type, level.subtype), level])
  );

  return COMPLIANCE_BEGIN_ACTION_SPECS.flatMap((spec) => {
    if (!allowedTypeSet.has(spec.key)) {
      return [];
    }

    const level = spec.preferredHierarchyKeys
      .map((candidateKey) => levelsByKey.get(candidateKey))
      .find(Boolean);

    const itemType = level?.type || spec.fallbackItemType;
    const itemSubtype = level?.subtype;
    const canCreate = canCreateAnyWorkflowItemType(
      canCreateItemType,
      itemType,
      spec.fallbackItemType,
      spec.key,
    );

    return [{
      key: spec.key,
      label: spec.label,
      sublabel: canCreate
        ? `Create and start a new ${spec.label.toLowerCase()} item`
        : `Open an assigned ${spec.label.toLowerCase()} item to continue working`,
      icon: level?.icon || spec.icon,
      color: spec.color,
      mode: canCreate ? 'create' : 'select_existing',
      displayItemType: spec.key,
      itemType,
      itemSubtype,
      initialStatus: resolveStartActionInitialStatus(itemType, itemSubtype, workflowStatuses),
      formCategory: 'compliance',
    } satisfies BeginActionStartAction];
  });
}

function inferBeginActionStartActions(
  profile: BeginActionProfile,
  allowedItemTypes: string[],
  workflowHierarchy: WorkflowHierarchyConfig,
  workflowStatuses: WorkflowStatusesConfig,
  canCreateItemType: CanCreateItemType,
): BeginActionStartAction[] {
  if (profile === 'compliance') {
    return inferComplianceStartActions(allowedItemTypes, workflowHierarchy, workflowStatuses, canCreateItemType);
  }

  return [];
}

function isAdvancedManufacturingTemplate(templateKey: string | null): boolean {
  const normalizedTemplateKey = normalizeWorkflowToken(templateKey);
  return normalizedTemplateKey === 'advanced_manufacturing'
    || normalizedTemplateKey === 'manufacturing_operations'
    || normalizedTemplateKey === 'manufacturing';
}

function isAdvancedManufacturingItemType(itemType: string): boolean {
  return [
    'production_order',
    'manufacturing_stage',
    'quality_check',
    'ncr',
    'outsourced_operation',
    'bom',
    'tooling',
  ].includes(normalizeWorkflowToken(itemType));
}

function shouldUseAdvancedManufacturingWorkflowDefaults(
  templateKey: string | null,
  allowedItemTypes: string[],
): boolean {
  return isAdvancedManufacturingTemplate(templateKey)
    || allowedItemTypes.some((itemType) => isAdvancedManufacturingItemType(itemType));
}

function buildAdvancedManufacturingStartActions(allowedItemTypes: string[]): BeginActionStartAction[] {
  const allowedTypeSet = new Set(allowedItemTypes.map((itemType) => normalizeWorkflowToken(itemType)).filter(Boolean));

  return ADVANCED_MANUFACTURING_START_ACTIONS.filter((action) => {
    const normalizedItemType = normalizeWorkflowToken(action.itemType);
    return allowedTypeSet.size === 0 || allowedTypeSet.has(normalizedItemType);
  });
}

function buildAdvancedManufacturingWorkflowSections(
  config: SmartTaskConfig,
  canCreateItemType: CanCreateItemType,
): BeginActionWorkflowSection[] {
  const allowedTypeSet = new Set(config.allowedItemTypes.map((itemType) => normalizeWorkflowToken(itemType)).filter(Boolean));
  const hasAllowedItemType = (...itemTypes: string[]) => itemTypes.some((itemType) => allowedTypeSet.has(normalizeWorkflowToken(itemType)));
  const workflowSections: BeginActionWorkflowSection[] = [];

  const addSection = (section: BeginActionWorkflowSection | null) => {
    if (!section || section.actions.length === 0) {
      return;
    }

    workflowSections.push({
      ...section,
      icon: section.icon || config.templateIcon,
      color: section.color || config.templateColor,
      templateKey: section.templateKey ?? config.templateKey,
      templateName: section.templateName ?? config.templateName,
      actions: dedupeBeginActionWorkflowSectionActions(section.actions),
    });
  };

  addSection({
    key: 'production_start',
    title: 'Production Start',
    description: 'Open the next production order, assigned stage, or QC gate to start the manufacturing flow.',
    icon: 'Factory',
    color: '#ea580c',
    actions: [
      hasAllowedItemType('production_order') ? buildPickerWorkflowSectionAction({
        key: 'production_orders',
        label: 'Work Orders',
        icon: 'Factory',
        color: '#ea580c',
        pickerMode: 'all',
        itemTypes: ['production_order'],
        startLabel: 'Start Work Order',
        emptyMessage: 'No production orders available to start',
      }) : null,
      hasAllowedItemType('manufacturing_stage') ? buildPickerWorkflowSectionAction({
        key: 'manufacturing_stages',
        label: 'Production Stages',
        icon: 'GitBranch',
        color: '#7c3aed',
        pickerMode: 'all',
        itemTypes: ['manufacturing_stage'],
        startLabel: 'Start Production Stage',
        emptyMessage: 'No manufacturing stages available to start',
      }) : null,
      hasAllowedItemType('quality_check') ? buildPickerWorkflowSectionAction({
        key: 'quality_checks',
        label: 'QC Checks',
        icon: 'ClipboardCheck',
        color: '#059669',
        pickerMode: 'all',
        itemTypes: ['quality_check'],
        startLabel: 'Start QC Check',
        emptyMessage: 'No quality checks available to start',
      }) : null,
    ].filter(isDefined),
  });

  addSection({
    key: 'stage_execution',
    title: 'Stage Execution',
    description: 'Run the active stage with traceable scans, live measurements, and controlled work instructions.',
    icon: 'Play',
    color: '#2563eb',
    actions: [
      (config.features.qrScanning || config.features.barcodeScanning) ? {
        key: 'scan_qr',
        label: 'Scan QR / Barcode',
        icon: 'QrCode',
        color: '#0891b2',
        action: 'scan_qr',
        mode: 'action',
        requiresActiveJob: true,
      } satisfies BeginActionWorkflowSectionAction : null,
      config.features.enableMeasurements ? {
        key: 'take_measurement',
        label: 'Capture Measurement',
        icon: 'Ruler',
        color: '#0891b2',
        action: 'take_measurement',
        mode: 'action',
        requiresActiveJob: true,
      } satisfies BeginActionWorkflowSectionAction : null,
      config.features.enableDrawingViewer ? {
        key: 'view_drawing',
        label: 'Open Drawing',
        icon: 'FileText',
        color: '#2563eb',
        action: 'view_drawing',
        mode: 'action',
        requiresActiveJob: true,
      } satisfies BeginActionWorkflowSectionAction : null,
      config.features.enableBOMViewer ? {
        key: 'view_bom',
        label: 'Open BOM',
        icon: 'Layers',
        color: '#7c3aed',
        action: 'view_bom',
        mode: 'action',
        requiresActiveJob: true,
      } satisfies BeginActionWorkflowSectionAction : null,
    ].filter(isDefined),
  });

  addSection({
    key: 'quality_compliance',
    title: 'Quality & Compliance',
    description: 'Inspect in-process work, attach evidence, and escalate defects before the line moves forward.',
    icon: 'ClipboardCheck',
    color: '#059669',
    actions: [
      config.features.enableQCInspection ? {
        key: 'run_qc',
        label: 'Record Inspection',
        icon: 'ClipboardCheck',
        color: '#059669',
        action: 'run_qc',
        mode: 'action',
        requiresActiveJob: true,
      } satisfies BeginActionWorkflowSectionAction : null,
      hasAllowedItemType('inspection') ? buildPickerWorkflowSectionAction({
        key: 'inspections',
        label: 'Inspections',
        icon: 'CheckSquare',
        color: '#d97706',
        pickerMode: 'all',
        itemTypes: ['inspection'],
        startLabel: 'Start Inspection',
        emptyMessage: 'No inspections available to start',
      }) : null,
      config.features.enableNCRReporting ? {
        key: 'log_ncr',
        label: 'Raise NCR',
        icon: 'FileWarning',
        color: '#dc2626',
        action: 'log_ncr',
        mode: 'action',
        requiresActiveJob: true,
      } satisfies BeginActionWorkflowSectionAction : null,
    ].filter(isDefined),
  });

  addSection({
    key: 'materials_documents',
    title: 'Materials & Documents',
    description: 'Reference the controlled BOM, drawings, and supporting documents needed for this stage.',
    icon: 'Package',
    color: '#7c3aed',
    actions: [
      config.features.enableBOMViewer ? {
        key: 'view_bom',
        label: 'View BOM',
        icon: 'Layers',
        color: '#7c3aed',
        action: 'view_bom',
        mode: 'action',
        requiresActiveJob: true,
      } satisfies BeginActionWorkflowSectionAction : null,
      config.features.enableDrawingViewer ? {
        key: 'view_drawing',
        label: 'View Drawing',
        icon: 'FileText',
        color: '#2563eb',
        action: 'view_drawing',
        mode: 'action',
        requiresActiveJob: true,
      } satisfies BeginActionWorkflowSectionAction : null,
      {
        key: 'generate_document',
        label: 'Documents',
        icon: 'FileText',
        color: '#8b5cf6',
        action: 'generate_document',
        mode: 'action',
        requiresActiveJob: true,
      } satisfies BeginActionWorkflowSectionAction,
    ].filter(isDefined),
  });

  addSection({
    key: 'exception_handling',
    title: 'Exception Handling',
    description: 'Capture blockers, defects, and supervisor-ready evidence before the issue cascades downstream.',
    icon: 'AlertTriangle',
    color: '#dc2626',
    actions: [
      config.features.enableNCRReporting ? {
        key: 'log_ncr',
        label: 'Log NCR',
        icon: 'FileWarning',
        color: '#dc2626',
        action: 'log_ncr',
        mode: 'action',
        requiresActiveJob: true,
      } satisfies BeginActionWorkflowSectionAction : null,
      {
        key: 'report_issue',
        label: 'Report Machine Issue',
        icon: 'MessageSquare',
        color: '#ea580c',
        action: 'report_issue',
        mode: 'action',
        requiresActiveJob: true,
      } satisfies BeginActionWorkflowSectionAction,
      {
        key: 'operational_form',
        label: 'Supervisor Review',
        icon: 'ClipboardCheck',
        color: '#4f46e5',
        action: 'operational_form',
        mode: 'action',
        requiresActiveJob: true,
      } satisfies BeginActionWorkflowSectionAction,
    ].filter(isDefined),
  });

  addSection({
    key: 'outsource_vendor',
    title: 'Outsource & Vendor',
    description: 'Track outsourced operations and vendor hand-offs as part of the manufacturing workflow.',
    icon: 'Truck',
    color: '#0f766e',
    actions: [
      hasAllowedItemType('outsourced_operation') ? buildPickerWorkflowSectionAction({
        key: 'outsourced_operations',
        label: 'Outsourced Operations',
        icon: 'Truck',
        color: '#0f766e',
        pickerMode: 'all',
        itemTypes: ['outsourced_operation'],
        startLabel: 'Open Outsourced Operation',
        emptyMessage: 'No outsourced operations available',
      }) : null,
      {
        key: 'generate_document',
        label: 'Vendor Documents',
        icon: 'FileText',
        color: '#8b5cf6',
        action: 'generate_document',
        mode: 'action',
        requiresActiveJob: true,
      } satisfies BeginActionWorkflowSectionAction,
    ].filter(isDefined),
  });

  addSection({
    key: 'close_release',
    title: 'Close & Release',
    description: 'Run the final QC checkpoint, complete the stage, and prepare the next hand-off or release.',
    icon: 'CheckCircle',
    color: '#16a34a',
    actions: [
      config.features.enableQCInspection ? {
        key: 'run_qc',
        label: 'Final QC Check',
        icon: 'ClipboardCheck',
        color: '#059669',
        action: 'run_qc',
        mode: 'action',
        requiresActiveJob: true,
      } satisfies BeginActionWorkflowSectionAction : null,
      config.features.enableStageManagement ? {
        key: 'complete_stage',
        label: 'Complete Stage',
        icon: 'Layers3',
        color: '#7c3aed',
        action: 'complete_stage',
        mode: 'action',
        requiresActiveJob: true,
      } satisfies BeginActionWorkflowSectionAction : null,
      {
        key: 'generate_document',
        label: 'Release Docs',
        icon: 'FileText',
        color: '#8b5cf6',
        action: 'generate_document',
        mode: 'action',
        requiresActiveJob: true,
      } satisfies BeginActionWorkflowSectionAction,
    ].filter(isDefined),
  });

  return workflowSections;
}

function isServiceOperationsTemplate(templateKey: string | null): boolean {
  return (templateKey || '').trim().toLowerCase() === 'service-operations';
}

function isFinancialManagementTemplate(templateKey: string | null): boolean {
  return (templateKey || '').trim().toLowerCase() === 'financial-management';
}

function isFleetManagementTemplate(templateKey: string | null | undefined): boolean {
  return (templateKey || '').trim().toLowerCase() === 'fleet-management';
}

function toTitleCase(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeDetailLayoutTabs(value: unknown): DetailLayoutTab[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => {
      if (typeof entry === 'string') {
        const key = entry.trim();
        if (!(DETAIL_TAB_KEYS as string[]).includes(key)) {
          return null;
        }

        return { key: key as DetailTabKey } satisfies DetailLayoutTab;
      }

      if (!entry || typeof entry !== 'object') {
        return null;
      }

      const rawTab = entry as UnknownRecord;
      const key = typeof rawTab.key === 'string' ? rawTab.key.trim() : '';

      if (!(DETAIL_TAB_KEYS as string[]).includes(key)) {
        return null;
      }

      return {
        key: key as DetailTabKey,
        label: typeof rawTab.label === 'string' && rawTab.label.trim().length > 0
          ? rawTab.label.trim()
          : undefined,
      } satisfies DetailLayoutTab;
    })
    .filter((tab): tab is DetailLayoutTab => Boolean(tab));
}

function normalizeDetailTabLabels(value: unknown): Partial<Record<DetailTabKey, string>> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value as UnknownRecord).reduce<Partial<Record<DetailTabKey, string>>>((acc, [key, label]) => {
    if ((DETAIL_TAB_KEYS as string[]).includes(key) && typeof label === 'string' && label.trim().length > 0) {
      acc[key as DetailTabKey] = label.trim();
    }
    return acc;
  }, {});
}

function buildDetailLayoutTabs(
  keys: DetailTabKey[],
  labels: Partial<Record<DetailTabKey, string>> = {},
): DetailLayoutTab[] {
  return keys.map((key) => ({
    key,
    label: labels[key] || DETAIL_TAB_LABELS[key],
  }));
}

function inferDetailProfile(
  templateKey: string | null,
  displayMode: DisplayMode,
  allowedItemTypes: string[],
  features: FeatureFlags,
): DetailProfile {
  const normalizedTemplateKey = (templateKey || '').toLowerCase();
  const normalizedItemTypes = allowedItemTypes.map((itemType) => itemType.toLowerCase());

  if (
    normalizedTemplateKey.includes('quote')
    || normalizedTemplateKey.includes('delivery')
    || normalizedItemTypes.some((itemType) => COMMERCIAL_DETAIL_ITEM_TYPES.has(itemType))
  ) {
    return 'commercial';
  }

  if (
    displayMode === 'inspection_centric'
    || normalizedTemplateKey.includes('inspection')
    || normalizedTemplateKey.includes('compliance')
    || features.enableDataValidation
    || normalizedItemTypes.some((itemType) => COMPLIANCE_DETAIL_ITEM_TYPES.has(itemType))
  ) {
    return 'compliance';
  }

  if (
    normalizedTemplateKey.includes('incident')
    || normalizedTemplateKey.includes('ticket')
    || normalizedItemTypes.some((itemType) => INCIDENT_DETAIL_ITEM_TYPES.has(itemType))
  ) {
    return 'incident';
  }

  if (displayMode === 'ticket_centric') {
    return 'incident';
  }

  return 'job';
}

function buildDefaultDetailLayout(
  profile: DetailProfile,
  terminology: TerminologyConfig,
): DetailLayoutConfig {
  const fallbackEntityLabel = terminology.workItem || terminology.job || 'Work Item';

  switch (profile) {
    case 'commercial':
      return {
        profile,
        entityLabel: 'Document',
        supportsWorkflowActions: false,
        tabs: buildDetailLayoutTabs(['details', 'materials', 'documents', 'comments', 'photos'], {
          materials: 'Items',
          documents: 'Documents',
        }),
      };

    case 'incident':
      return {
        profile,
        entityLabel: 'Incident',
        supportsWorkflowActions: true,
        tabs: buildDetailLayoutTabs(['details', 'photos', 'comments', 'work-breakdown', 'documents']),
      };

    case 'compliance':
      return {
        profile,
        entityLabel: 'Inspection',
        supportsWorkflowActions: true,
        tabs: buildDetailLayoutTabs(['details', 'work-breakdown', 'photos', 'comments', 'documents']),
      };

    case 'general':
      return {
        profile,
        entityLabel: fallbackEntityLabel,
        supportsWorkflowActions: true,
        tabs: buildDetailLayoutTabs(['details', 'work-breakdown', 'photos', 'comments', 'documents']),
      };

    case 'job':
    default:
      return {
        profile: 'job',
        entityLabel: terminology.job || fallbackEntityLabel,
        supportsWorkflowActions: true,
        tabs: buildDetailLayoutTabs(['details', 'work-breakdown', 'photos', 'comments', 'materials', 'expenses', 'documents']),
      };
  }
}

function resolveDetailLayoutConfig(
  smartTaskTc: UnknownRecord,
  detailProfile: DetailProfile,
  terminology: TerminologyConfig,
): DetailLayoutConfig {
  const defaultLayout = buildDefaultDetailLayout(detailProfile, terminology);
  const rawDetailLayout = (smartTaskTc.detail_layout && typeof smartTaskTc.detail_layout === 'object' && !Array.isArray(smartTaskTc.detail_layout))
    ? smartTaskTc.detail_layout as UnknownRecord
    : (smartTaskTc.detailLayout && typeof smartTaskTc.detailLayout === 'object' && !Array.isArray(smartTaskTc.detailLayout))
      ? smartTaskTc.detailLayout as UnknownRecord
      : {};

  const rawTabs = normalizeDetailLayoutTabs(rawDetailLayout.tabs);
  const rawLabels = normalizeDetailTabLabels(rawDetailLayout.tab_labels ?? rawDetailLayout.tabLabels);
  const tabs = (rawTabs.length > 0 ? rawTabs : defaultLayout.tabs).map((tab) => ({
    key: tab.key,
    label: tab.label || rawLabels[tab.key] || defaultLayout.tabs.find((defaultTab) => defaultTab.key === tab.key)?.label || DETAIL_TAB_LABELS[tab.key],
  }));

  return {
    profile: detailProfile,
    entityLabel: typeof rawDetailLayout.entity_label === 'string' && rawDetailLayout.entity_label.trim().length > 0
      ? rawDetailLayout.entity_label.trim()
      : typeof rawDetailLayout.entityLabel === 'string' && rawDetailLayout.entityLabel.trim().length > 0
        ? rawDetailLayout.entityLabel.trim()
        : defaultLayout.entityLabel,
    supportsWorkflowActions: typeof rawDetailLayout.supports_workflow_actions === 'boolean'
      ? rawDetailLayout.supports_workflow_actions
      : typeof rawDetailLayout.supportsWorkflowActions === 'boolean'
        ? rawDetailLayout.supportsWorkflowActions
        : defaultLayout.supportsWorkflowActions,
    tabs,
  };
}

function inferDetailProfileForItemType(itemType: string | undefined, fallbackProfile: DetailProfile): DetailProfile {
  const normalizedItemType = (itemType || '').toLowerCase();

  if (COMMERCIAL_DETAIL_ITEM_TYPES.has(normalizedItemType)) {
    return 'commercial';
  }

  if (INCIDENT_DETAIL_ITEM_TYPES.has(normalizedItemType)) {
    return 'incident';
  }

  if (COMPLIANCE_DETAIL_ITEM_TYPES.has(normalizedItemType)) {
    return 'compliance';
  }

  return fallbackProfile;
}

function deriveDetailEntityLabel(
  workItem: Pick<WorkItem, 'item_type' | 'item_subtype'> | null | undefined,
  fallbackLabel: string,
): string {
  const itemType = workItem?.item_type?.trim();
  if (!itemType) {
    return fallbackLabel;
  }

  const subtype = workItem?.item_subtype?.trim();
  return toTitleCase(subtype && subtype.length > 0 ? subtype : itemType);
}

export function resolveDetailLayoutForWorkItem(
  config: Pick<SmartTaskConfig, 'detailProfile' | 'detailLayout' | 'terminology'>,
  workItem?: Pick<WorkItem, 'item_type' | 'item_subtype'> | null,
): DetailLayoutConfig {
  const effectiveProfile = inferDetailProfileForItemType(workItem?.item_type, config.detailProfile || 'job');
  const fallbackLayout = buildDefaultDetailLayout(effectiveProfile, config.terminology);
  const baseLayout = config.detailLayout?.profile === effectiveProfile
    ? config.detailLayout
    : fallbackLayout;

  return {
    profile: effectiveProfile,
    entityLabel: deriveDetailEntityLabel(workItem, baseLayout.entityLabel || fallbackLayout.entityLabel || config.terminology.workItem || config.terminology.job || 'Work Item'),
    supportsWorkflowActions: baseLayout.supportsWorkflowActions ?? fallbackLayout.supportsWorkflowActions,
    tabs: (baseLayout.tabs?.length > 0 ? baseLayout.tabs : fallbackLayout.tabs).map((tab) => ({
      key: tab.key,
      label: tab.label || DETAIL_TAB_LABELS[tab.key],
    })),
  };
}

function shouldIncludeToolsEquipmentShortcut(
  profile: BeginActionProfile,
  templateKey: string | null,
  allowedItemTypes: string[],
  quickActions: QuickAction[],
): boolean {
  if (profile === 'field_operations' || profile === 'asset_inventory') {
    return true;
  }

  const normalizedTemplateKey = (templateKey || '').toLowerCase();
  if (normalizedTemplateKey.includes('asset') || normalizedTemplateKey.includes('inventory')) {
    return true;
  }

  const normalizedItemTypes = allowedItemTypes.map((itemType) => itemType.toLowerCase());
  if (normalizedItemTypes.some((itemType) => ['asset', 'inventory', 'tooling'].includes(itemType))) {
    return true;
  }

  return quickActions.some((quickAction) => BEGIN_ACTION_TOOLS_PANEL_SIGNAL_KEYS.has(quickAction.key.toLowerCase()));
}

function buildTemplateBeginActionShortcutPanels(
  templateKey: string | null,
  quickActions: QuickAction[],
): BeginActionPanelAction[] {
  const panelActions = quickActions.map<BeginActionPanelAction | null>((quickAction) => {
    const actionKey = typeof quickAction.action === 'string' && quickAction.action.trim().length > 0
      ? quickAction.action.trim()
      : quickAction.key;

    if (actionKey === 'generate_quote' && !isFinancialManagementTemplate(templateKey)) {
      return null;
    }

    const defaultAction = DEFAULT_BEGIN_ACTION_PANEL_ACTIONS[actionKey];

    if (!defaultAction || !defaultAction.section || actionKey === 'tools_equipment' || actionKey === 'fleet_manager') {
      return null;
    }

    const panelAction: BeginActionPanelAction = {
      ...defaultAction,
      key: quickAction.key,
      label: quickAction.label || defaultAction.label,
      icon: quickAction.icon || defaultAction.icon,
      color: quickAction.color || defaultAction.color,
      action: actionKey,
      requiresActiveJob: quickAction.requiresActiveJob ?? defaultAction.requiresActiveJob,
      requiresCheckedIn: quickAction.requiresCheckedIn ?? defaultAction.requiresCheckedIn,
    };

    return panelAction;
  });

  return dedupeBeginActionPanelActions(panelActions.filter(isDefined)).slice(0, MAX_TEMPLATE_BEGIN_ACTION_SHORTCUTS);
}

function inferBeginActionProfile(
  templateKey: string | null,
  features: FeatureFlags,
  rules: OperationalRules,
  allowedItemTypes: string[],
  displayMode: DisplayMode,
  quickActions: QuickAction[],
): BeginActionProfile {
  const normalizedTemplateKey = (templateKey || '').toLowerCase();
  const normalizedItemTypes = allowedItemTypes.map((itemType) => itemType.toLowerCase());
  const normalizedQuickActionKeys = quickActions.map((quickAction) => quickAction.key.toLowerCase());

  if (normalizedTemplateKey === 'service-operations') return 'field_operations';
  if (normalizedTemplateKey === 'asset-inventory-management') return 'asset_inventory';
  if (normalizedTemplateKey === 'legal-services' || normalizedTemplateKey === 'crm-management') return 'knowledge_worker';
  if (normalizedTemplateKey === 'compliance-manager') return 'compliance';

  if (
    normalizedTemplateKey.includes('logistics')
    || normalizedTemplateKey.includes('delivery')
    || normalizedTemplateKey.includes('waste')
    || rules.requireTravel
    || rules.requireVehicle
    || features.routes
    || displayMode === 'route_centric'
    || displayMode === 'appointment_centric'
  ) {
    return 'field_operations';
  }

  if (
    normalizedTemplateKey.includes('asset')
    || normalizedTemplateKey.includes('inventory')
    || normalizedQuickActionKeys.some((actionKey) => BEGIN_ACTION_RESOURCE_SIGNAL_KEYS.has(actionKey))
    || normalizedItemTypes.some((itemType) => ['asset', 'inventory', 'tooling'].includes(itemType))
  ) {
    return 'asset_inventory';
  }

  if (normalizedItemTypes.length > 0 && normalizedItemTypes.every((itemType) => itemType === 'asset' || itemType === 'inventory')) {
    return 'asset_inventory';
  }

  if (
    features.enableNCRReporting
    || features.enableQCInspection
    || features.enableComplianceIncidents
    || features.enableComplianceAudits
    || features.enableComplianceCertifications
    || features.enableComplianceInspections
    || features.enableComplianceHazards
  ) {
    return 'compliance';
  }

  if (normalizedItemTypes.some((itemType) => ['lead', 'opportunity', 'contact'].includes(itemType))) {
    return 'knowledge_worker';
  }

  return 'general';
}

function buildDefaultBeginActionConfig(
  templateKey: string | null,
  features: FeatureFlags,
  rules: OperationalRules,
  allowedItemTypes: string[],
  displayMode: DisplayMode,
  quickActions: QuickAction[],
): BeginActionConfig {
  const profile = inferBeginActionProfile(templateKey, features, rules, allowedItemTypes, displayMode, quickActions);
  const templateShortcutPanels = buildTemplateBeginActionShortcutPanels(templateKey, quickActions);

  let sections: BeginActionSectionKey[] = [];
  let panelActions: BeginActionPanelAction[] = [];

  switch (profile) {
    case 'field_operations':
      // Non-drivers get no vehicle_fleet section or fleet-manager shortcut —
      // vehicle selection and inspections belong to the driver.
      sections = rules.isNonDriver ? ['resource_tools'] : ['vehicle_fleet', 'resource_tools'];
      panelActions = [
        { ...DEFAULT_BEGIN_ACTION_PANEL_ACTIONS.tools_equipment },
      ];

      if (!isServiceOperationsTemplate(templateKey)) {
        sections.push('quick_access');
        panelActions.push({ ...DEFAULT_BEGIN_ACTION_PANEL_ACTIONS.smart_clock });
        if (!rules.isNonDriver) {
          panelActions.push({ ...DEFAULT_BEGIN_ACTION_PANEL_ACTIONS.fleet_manager });
        }
      }
      break;

    case 'asset_inventory':
      sections = ['resource_tools'];
      panelActions = [{ ...DEFAULT_BEGIN_ACTION_PANEL_ACTIONS.tools_equipment }];
      break;

    case 'knowledge_worker':
    case 'compliance':
      sections = [];
      panelActions = [];
      break;

    default:
      sections = [];
      panelActions = [];
      if (!rules.isNonDriver && (rules.requireVehicle || hasAnyInspectionRules(rules) || features.enableVehicleChange || features.enableOdometerCapture)) {
        sections.push('vehicle_fleet');
      }
      break;
  }

  if (rules.requireDigitalLogbook) {
    sections.push('quick_access');
    panelActions.push({ ...DEFAULT_BEGIN_ACTION_PANEL_ACTIONS.digital_logbook });
  }

  if (
    shouldIncludeToolsEquipmentShortcut(profile, templateKey, allowedItemTypes, quickActions)
    && !panelActions.some((panelAction) => (panelAction.action || panelAction.key) === 'tools_equipment')
  ) {
    sections.push('resource_tools');
    panelActions.push({ ...DEFAULT_BEGIN_ACTION_PANEL_ACTIONS.tools_equipment });
  }

  if (!isServiceOperationsTemplate(templateKey) && templateShortcutPanels.length > 0) {
    sections.push('quick_access');
    panelActions.push(...templateShortcutPanels);
  }

  return {
    profile,
    sections: Array.from(new Set(sections)),
    panelActions: dedupeBeginActionPanelActions(panelActions),
  };
}

function resolveBeginActionConfig(
  smartTaskTc: UnknownRecord,
  templateKey: string | null,
  features: FeatureFlags,
  rules: OperationalRules,
  allowedItemTypes: string[],
  displayMode: DisplayMode,
  quickActions: QuickAction[],
  workflowHierarchy: WorkflowHierarchyConfig,
  workflowStatuses: WorkflowStatusesConfig,
  canCreateItemType: CanCreateItemType,
): BeginActionConfig {
  const defaultConfig = buildDefaultBeginActionConfig(templateKey, features, rules, allowedItemTypes, displayMode, quickActions);
  const rawBeginAction = (smartTaskTc.begin_action && typeof smartTaskTc.begin_action === 'object' && !Array.isArray(smartTaskTc.begin_action))
    ? smartTaskTc.begin_action as UnknownRecord
    : (smartTaskTc.beginAction && typeof smartTaskTc.beginAction === 'object' && !Array.isArray(smartTaskTc.beginAction))
      ? smartTaskTc.beginAction as UnknownRecord
      : {};

  const hasRawSections = Object.prototype.hasOwnProperty.call(rawBeginAction, 'sections');
  const rawSections = normalizeBeginActionSections(rawBeginAction.sections);
  const rawPanelActions = normalizeBeginActionPanelActions(rawBeginAction.panel_actions ?? rawBeginAction.panelActions);

  const sections = hasRawSections ? [...rawSections] : [...defaultConfig.sections];

  if (!hasRawSections) {
    for (const panelAction of rawPanelActions) {
      if (panelAction.section && !sections.includes(panelAction.section)) {
        sections.push(panelAction.section);
      }
    }
  }

  const panelActions = dedupeBeginActionPanelActions([
    ...defaultConfig.panelActions,
    ...rawPanelActions,
  ]).filter((panelAction) => {
    const actionKey = (panelAction.action || panelAction.key || '').trim().toLowerCase();

    if (isServiceOperationsTemplate(templateKey) && panelAction.section === 'quick_access' && actionKey !== 'digital_logbook') {
      return false;
    }

    if (actionKey === 'generate_quote' && !isFinancialManagementTemplate(templateKey)) {
      return false;
    }

    return !panelAction.section || sections.includes(panelAction.section);
  });

  const rawProfile = typeof rawBeginAction.profile === 'string' ? rawBeginAction.profile.trim() : '';
  const profile = (BEGIN_ACTION_PROFILES as string[]).includes(rawProfile)
    ? rawProfile as BeginActionProfile
    : defaultConfig.profile;

  const rawStartActions = normalizeBeginActionStartActions(rawBeginAction.start_actions ?? rawBeginAction.startActions);
  const startActions = dedupeBeginActionStartActions(
    rawStartActions.length > 0
      ? rawStartActions
      : shouldUseAdvancedManufacturingWorkflowDefaults(templateKey, allowedItemTypes)
        ? buildAdvancedManufacturingStartActions(allowedItemTypes)
        : inferBeginActionStartActions(profile, allowedItemTypes, workflowHierarchy, workflowStatuses, canCreateItemType)
  );
  const workflowSections = normalizeBeginActionWorkflowSections(
    rawBeginAction.workflow_sections
    ?? rawBeginAction.workflowSections
    ?? smartTaskTc.workflow_sections
    ?? smartTaskTc.workflowSections
  );

  const keepQuickAccessForDigitalLogbook = panelActions.some((panelAction) => (
    panelAction.section === 'quick_access' && (panelAction.action || panelAction.key || '').trim().toLowerCase() === 'digital_logbook'
  ));

  const normalizedSections = isServiceOperationsTemplate(templateKey)
    ? sections.filter((section) => section !== 'quick_access' || keepQuickAccessForDigitalLogbook)
    : sections;

  return {
    profile,
    sections: normalizedSections,
    panelActions,
    startActions,
    workflowSections: workflowSections.length > 0 ? workflowSections : undefined,
  };
}

const DEFAULT_WORKFLOW_SECTION_TITLES: Record<string, string> = {
  'service-operations': 'Service Operations Workflows',
  'financial-management': 'Sales Management Workflows',
  'fleet-management': 'Fleet Management Workflows',
  'asset-inventory-management': 'Asset & Inventory Management',
  'compliance-manager': 'Quality Assurance Workflows',
  'qa-management': 'Quality Assurance Workflows',
  'manufacturing-operations': 'Advanced Manufacturing Workflows',
  'advanced-manufacturing': 'Advanced Manufacturing Workflows',
};

const DEFAULT_WORKFLOW_SECTION_DESCRIPTIONS: Record<string, string> = {
  'service-operations': 'Service calls, appointments, and day-to-day field work.',
  'financial-management': 'Quotes, invoices, bills, and sales document workflows.',
  'fleet-management': 'Vehicle allocation plus standalone fuel, toll, and maintenance capture.',
  'asset-inventory-management': 'Asset tracking, vehicle handovers, inspections, and inventory-linked work.',
  'compliance-manager': 'Incidents, audits, inspections, and compliance workflows.',
  'qa-management': 'Incidents, audits, inspections, and compliance workflows.',
  'manufacturing-operations': 'Production orders, stage execution, quality checks, materials, and vendor hand-offs.',
  'advanced-manufacturing': 'Production orders, stage execution, quality checks, materials, and vendor hand-offs.',
};

function toPluralLabel(label: string, fallback: string): string {
  const trimmedLabel = label.trim();
  if (!trimmedLabel) {
    return fallback;
  }

  return /s$/i.test(trimmedLabel) ? trimmedLabel : `${trimmedLabel}s`;
}

function buildPickerWorkflowSectionAction(input: {
  key: string;
  label: string;
  icon: string;
  color: string;
  pickerMode: BeginActionPickerMode;
  itemTypes?: string[];
  startLabel: string;
  emptyMessage: string;
  requiresVehicle?: boolean;
}): BeginActionWorkflowSectionAction {
  return {
    key: input.key,
    label: input.label,
    icon: input.icon,
    color: input.color,
    pickerMode: input.pickerMode,
    itemTypes: input.itemTypes,
    startLabel: input.startLabel,
    emptyMessage: input.emptyMessage,
    requiresVehicle: input.requiresVehicle,
    mode: 'select_existing',
  } satisfies BeginActionWorkflowSectionAction;
}

function buildCreateWorkflowSectionAction(input: {
  key: string;
  label: string;
  icon: string;
  color: string;
  itemType: string;
  description?: string;
  mode?: 'create' | 'select_existing';
  pickerMode?: BeginActionPickerMode;
  action?: string;
  startLabel?: string;
  emptyMessage?: string;
}): BeginActionWorkflowSectionAction {
  return {
    key: input.key,
    label: input.label,
    icon: input.icon,
    color: input.color,
    description: input.description,
    itemTypes: [input.itemType],
    mode: input.mode || 'create',
    pickerMode: input.pickerMode,
    action: input.action,
    startLabel: input.startLabel,
    emptyMessage: input.emptyMessage,
  } satisfies BeginActionWorkflowSectionAction;
}

function buildChildEntryWorkflowSectionAction(input: {
  key: string;
  label: string;
  icon: string;
  color: string;
  childEntryType: ChildEntryType;
  description?: string;
  requiresVehicle?: boolean;
  childEntryParentMode?: ChildEntryParentMode;
}): BeginActionWorkflowSectionAction {
  return {
    key: input.key,
    label: input.label,
    icon: input.icon,
    color: input.color,
    description: input.description,
    childEntryType: input.childEntryType,
    childEntryParentMode: input.childEntryParentMode,
    requiresVehicle: input.requiresVehicle,
    mode: 'action',
  } satisfies BeginActionWorkflowSectionAction;
}

function buildActionWorkflowSectionAction(input: {
  key: string;
  label: string;
  icon: string;
  color: string;
  action: string;
  description?: string;
  requiresVehicle?: boolean;
}): BeginActionWorkflowSectionAction {
  return {
    key: input.key,
    label: input.label,
    icon: input.icon,
    color: input.color,
    action: input.action,
    description: input.description,
    requiresVehicle: input.requiresVehicle,
    mode: 'action',
  } satisfies BeginActionWorkflowSectionAction;
}

function buildWorkflowSectionActionForItemType(
  itemType: string,
  config: Pick<SmartTaskConfig, 'terminology'>,
  canCreateItemType: CanCreateItemType,
): BeginActionWorkflowSectionAction | null {
  const normalizedItemType = normalizeWorkflowToken(itemType);
  if (!normalizedItemType) {
    return null;
  }

  switch (normalizedItemType) {
    case 'job':
      return buildPickerWorkflowSectionAction({
        key: 'jobs',
        label: toPluralLabel(config.terminology.jobs || config.terminology.job || 'Job', 'Jobs'),
        icon: 'Briefcase',
        color: '#2563eb',
        pickerMode: 'job',
        startLabel: `Start ${config.terminology.job || 'Job'}`,
        emptyMessage: `No ${(config.terminology.jobs || 'jobs').toLowerCase()} available to start`,
      });

    case 'appointment': {
      const appointmentLabel = config.terminology.appointment || 'Appointment';
      if (canCreateItemType(normalizedItemType)) {
        return buildCreateWorkflowSectionAction({
          key: 'appointments',
          label: toPluralLabel(appointmentLabel, 'Appointments'),
          icon: 'Calendar',
          color: '#7c3aed',
          itemType: 'appointment',
          description: `Schedule a new ${appointmentLabel.toLowerCase()}.`,
        });
      }

      return buildPickerWorkflowSectionAction({
        key: 'appointments',
        label: toPluralLabel(appointmentLabel, 'Appointments'),
        icon: 'Calendar',
        color: '#7c3aed',
        pickerMode: 'appointment',
        startLabel: `Start ${appointmentLabel}`,
        emptyMessage: `No ${toPluralLabel(appointmentLabel, 'appointments').toLowerCase()} available to start`,
      });
    }

    case 'task':
      return buildPickerWorkflowSectionAction({
        key: 'tasks',
        label: toPluralLabel(config.terminology.tasks || config.terminology.task || 'Task', 'Tasks'),
        icon: 'ClipboardList',
        color: '#0891b2',
        pickerMode: 'task',
        startLabel: `Start ${config.terminology.task || 'Task'}`,
        emptyMessage: `No ${(config.terminology.tasks || 'tasks').toLowerCase()} available to start`,
      });

    case 'route':
      return buildPickerWorkflowSectionAction({
        key: 'routes',
        label: 'Routes',
        icon: 'Route',
        color: '#0f766e',
        pickerMode: 'route',
        startLabel: 'Start Route',
        emptyMessage: 'No routes available to start',
        requiresVehicle: true,
      });

    case 'incident':
      return buildPickerWorkflowSectionAction({
        key: 'incidents',
        label: 'Incidents',
        icon: 'AlertTriangle',
        color: '#dc2626',
        pickerMode: 'incident',
        startLabel: 'Start Incident',
        emptyMessage: 'No incidents available to start',
      });

    case 'audit':
      return buildPickerWorkflowSectionAction({
        key: 'audits',
        label: 'Audits',
        icon: 'ClipboardCheck',
        color: '#059669',
        pickerMode: 'audit',
        startLabel: 'Start Audit',
        emptyMessage: 'No audits available to start',
      });

    case 'certification':
    case 'certificate':
      return buildPickerWorkflowSectionAction({
        key: 'certifications',
        label: 'Certifications',
        icon: 'Shield',
        color: '#4f46e5',
        pickerMode: 'certification',
        itemTypes: ['certification', 'certificate'],
        startLabel: 'Start Certification',
        emptyMessage: 'No certifications available to start',
      });

    case 'inspection':
      return buildPickerWorkflowSectionAction({
        key: 'inspections',
        label: 'Inspections',
        icon: 'CheckSquare',
        color: '#d97706',
        pickerMode: 'inspection',
        startLabel: 'Start Inspection',
        emptyMessage: 'No inspections available to start',
      });

    case 'hazard':
    case 'hazard_report':
      return buildPickerWorkflowSectionAction({
        key: 'hazards',
        label: 'Hazards',
        icon: 'FileWarning',
        color: '#ea580c',
        pickerMode: 'hazard',
        itemTypes: ['hazard', 'hazard_report'],
        startLabel: 'Start Hazard',
        emptyMessage: 'No hazards available to start',
      });

    case 'quote':
      if (!canCreateItemType(normalizedItemType)) {
        return buildPickerWorkflowSectionAction({
          key: 'quotes',
          label: 'Quotes',
          icon: 'FileText',
          color: '#0ea5e9',
          pickerMode: 'all',
          itemTypes: ['quote'],
          startLabel: 'Open Quote',
          emptyMessage: 'No quotes available to open',
        });
      }

      return buildCreateWorkflowSectionAction({
        key: 'quotes',
        label: 'Quotes',
        icon: 'FileText',
        color: '#0ea5e9',
        itemType: 'quote',
        description: 'Create a new quote draft.',
      });

    case 'proposal':
      if (!canCreateItemType(normalizedItemType)) {
        return buildPickerWorkflowSectionAction({
          key: 'proposals',
          label: 'Proposals',
          icon: 'Sparkles',
          color: '#8b5cf6',
          pickerMode: 'all',
          itemTypes: ['proposal'],
          startLabel: 'Open Proposal',
          emptyMessage: 'No proposals available to open',
        });
      }

      return buildCreateWorkflowSectionAction({
        key: 'proposals',
        label: 'Proposals',
        icon: 'Sparkles',
        color: '#8b5cf6',
        itemType: 'proposal',
        description: 'Create a new AI-powered proposal.',
      });

    case 'invoice':
      if (!canCreateItemType(normalizedItemType)) {
        return buildPickerWorkflowSectionAction({
          key: 'invoices',
          label: 'Invoices',
          icon: 'Receipt',
          color: '#16a34a',
          pickerMode: 'all',
          itemTypes: ['invoice'],
          startLabel: 'Open Invoice',
          emptyMessage: 'No invoices available to open',
        });
      }

      return buildCreateWorkflowSectionAction({
        key: 'invoices',
        label: 'Invoices',
        icon: 'Receipt',
        color: '#16a34a',
        itemType: 'invoice',
        description: 'Create a new invoice draft.',
      });

    case 'expense':
      if (!canCreateItemType(normalizedItemType)) {
        return buildPickerWorkflowSectionAction({
          key: 'expenses',
          label: 'Expenses',
          icon: 'Receipt',
          color: '#f97316',
          pickerMode: 'all',
          itemTypes: ['expense'],
          startLabel: 'Open Expense',
          emptyMessage: 'No expenses available to open',
        });
      }

      return buildCreateWorkflowSectionAction({
        key: 'expenses',
        label: 'Expenses',
        icon: 'Receipt',
        color: '#f97316',
        itemType: 'expense',
        description: 'Create a new expense draft.',
      });

    case 'credit_note':
      if (!canCreateItemType(normalizedItemType)) {
        return buildPickerWorkflowSectionAction({
          key: 'credit_notes',
          label: 'Credit Notes',
          icon: 'Receipt',
          color: '#7c3aed',
          pickerMode: 'all',
          itemTypes: ['credit_note'],
          startLabel: 'Open Credit Note',
          emptyMessage: 'No credit notes available to open',
        });
      }

      return buildCreateWorkflowSectionAction({
        key: 'credit_notes',
        label: 'Credit Notes',
        icon: 'Receipt',
        color: '#7c3aed',
        itemType: 'credit_note',
        description: 'Create a new credit note draft.',
      });

    case 'asset':
      return buildPickerWorkflowSectionAction({
        key: 'assets',
        label: 'Assets',
        icon: 'Package',
        color: '#2563eb',
        pickerMode: 'all',
        itemTypes: ['asset'],
        startLabel: 'Start Asset Workflow',
        emptyMessage: 'No assets available to start',
      });

    case 'inventory':
      return buildPickerWorkflowSectionAction({
        key: 'inventory',
        label: 'Inventory',
        icon: 'Layers',
        color: '#7c3aed',
        pickerMode: 'all',
        itemTypes: ['inventory'],
        startLabel: 'Start Inventory Workflow',
        emptyMessage: 'No inventory items available to start',
      });

    case 'ticket':
      return buildPickerWorkflowSectionAction({
        key: 'tickets',
        label: 'Tickets',
        icon: 'AlertTriangle',
        color: '#ea580c',
        pickerMode: 'all',
        itemTypes: ['ticket'],
        startLabel: 'Start Ticket',
        emptyMessage: 'No tickets available to start',
      });

    case 'project':
      return buildPickerWorkflowSectionAction({
        key: 'projects',
        label: 'Projects',
        icon: 'Layers',
        color: '#4f46e5',
        pickerMode: 'all',
        itemTypes: ['project'],
        startLabel: 'Start Project',
        emptyMessage: 'No projects available to start',
      });

    default:
      return buildPickerWorkflowSectionAction({
        key: normalizedItemType,
        label: toTitleCase(normalizedItemType),
        icon: 'Layers',
        color: '#64748b',
        pickerMode: 'all',
        itemTypes: [normalizedItemType],
        startLabel: `Start ${toTitleCase(normalizedItemType)}`,
        emptyMessage: `No ${toTitleCase(normalizedItemType).toLowerCase()} items available to start`,
      });
  }
}

function buildWorkflowSectionActionFromStartAction(startAction: BeginActionStartAction): BeginActionWorkflowSectionAction {
  const normalizedDisplayType = normalizeWorkflowToken(startAction.displayItemType || startAction.itemType);
  const pickerMode = normalizedDisplayType === 'incident'
    ? 'incident'
    : normalizedDisplayType === 'audit'
      ? 'audit'
      : normalizedDisplayType === 'certification' || normalizedDisplayType === 'certificate'
        ? 'certification'
        : normalizedDisplayType === 'inspection'
          ? 'inspection'
          : normalizedDisplayType === 'hazard'
            ? 'hazard'
            : normalizedDisplayType === 'appointment'
              ? 'appointment'
              : normalizedDisplayType === 'task'
                ? 'task'
                : normalizedDisplayType === 'job'
                  ? 'job'
                  : 'all';

  return {
    key: startAction.key,
    label: startAction.label,
    description: startAction.sublabel,
    icon: startAction.icon,
    color: startAction.color,
    mode: startAction.mode,
    pickerMode: startAction.mode === 'select_existing' ? pickerMode : undefined,
    itemTypes: [startAction.itemType],
    startLabel: startAction.mode === 'select_existing'
      ? `Start ${startAction.label.replace(/^Start\s+/i, '')}`
      : undefined,
    emptyMessage: startAction.mode === 'select_existing'
      ? `No ${startAction.label.replace(/^Start\s+/i, '').toLowerCase()} items available to start`
      : undefined,
  } satisfies BeginActionWorkflowSectionAction;
}

function buildFallbackWorkflowSectionActions(config: SmartTaskConfig, canCreateItemType: CanCreateItemType): BeginActionWorkflowSectionAction[] {
  const actions: BeginActionWorkflowSectionAction[] = [];

  for (const startAction of config.beginAction.startActions || []) {
    actions.push(buildWorkflowSectionActionFromStartAction(startAction));
  }

  if (actions.length === 0) {
    for (const itemType of Array.from(new Set(config.allowedItemTypes.map((value) => normalizeWorkflowToken(value)).filter(Boolean)))) {
      const action = buildWorkflowSectionActionForItemType(itemType, config, canCreateItemType);
      if (action) {
        actions.push(action);
      }
    }
  }

  for (const quickAction of config.quickActions || []) {
    if (quickAction.requiresActiveJob || quickAction.requiresCheckedIn) {
      continue;
    }

    const actionKey = typeof quickAction.action === 'string' && quickAction.action.trim().length > 0
      ? quickAction.action.trim()
      : quickAction.key;
    const pickerMode = getPickerModeForActionIntent(actionKey);

    if (pickerMode) {
      actions.push({
        key: quickAction.key,
        label: quickAction.label,
        icon: quickAction.icon,
        color: quickAction.color,
        pickerMode,
        mode: 'select_existing',
      });
      continue;
    }

    if (isCoreWorkflowLikeTemplateAction(actionKey)) {
      continue;
    }

    actions.push({
      key: quickAction.key,
      label: quickAction.label,
      icon: quickAction.icon,
      color: quickAction.color,
      action: actionKey,
      mode: 'action',
    });
  }

  return dedupeBeginActionWorkflowSectionActions(actions);
}

function buildDefaultWorkflowSections(config: SmartTaskConfig, canCreateItemType: CanCreateItemType): BeginActionWorkflowSection[] {
  if (shouldUseAdvancedManufacturingWorkflowDefaults(config.templateKey, config.allowedItemTypes)) {
    const advancedManufacturingSections = buildAdvancedManufacturingWorkflowSections(config, canCreateItemType);
    if (advancedManufacturingSections.length > 0) {
      return advancedManufacturingSections;
    }
  }

  const sectionActions = buildFallbackWorkflowSectionActions(config, canCreateItemType);
  if (sectionActions.length === 0) {
    return [];
  }

  const normalizedTemplateKey = normalizeWorkflowToken(config.templateKey || config.templateName || 'workflow');

  return [{
    key: normalizedTemplateKey || 'workflow',
    title: DEFAULT_WORKFLOW_SECTION_TITLES[normalizedTemplateKey] || config.templateName || 'Workflow',
    description: DEFAULT_WORKFLOW_SECTION_DESCRIPTIONS[normalizedTemplateKey],
    icon: config.templateIcon,
    color: config.templateColor,
    templateKey: config.templateKey,
    templateName: config.templateName,
    actions: sectionActions,
  } satisfies BeginActionWorkflowSection];
}

const MANUFACTURING_BEGIN_ACTION_LAYOUT_KEYS = new Set(
  [
    'production_start',
    'stage_execution',
    'quality_compliance',
    'materials_documents',
    'exception_handling',
    'outsource_vendor',
    'close_release',
  ].map((key) => normalizeWorkflowToken(key)),
);

function beginActionSectionLooksManufacturing(section: BeginActionWorkflowSection): boolean {
  return MANUFACTURING_BEGIN_ACTION_LAYOUT_KEYS.has(normalizeWorkflowToken(section.key));
}

function ensureFinancialManagementProposalAction(
  workflowSections: BeginActionWorkflowSection[],
  config: SmartTaskConfig,
  canCreateItemType: CanCreateItemType,
): BeginActionWorkflowSection[] {
  if (!isFinancialManagementTemplate(config.templateKey) || !config.features.proposalCreation) {
    return workflowSections;
  }

  const proposalAction = buildWorkflowSectionActionForItemType('proposal', config, canCreateItemType);
  if (!proposalAction) {
    return workflowSections;
  }

  const sectionIndex = workflowSections.findIndex((section) => (
    isFinancialManagementTemplate(section.templateKey || section.key)
  ));

  if (sectionIndex === -1) {
    return [
      ...workflowSections,
      {
        key: 'financial-management',
        title: DEFAULT_WORKFLOW_SECTION_TITLES['financial-management'],
        description: DEFAULT_WORKFLOW_SECTION_DESCRIPTIONS['financial-management'],
        icon: config.templateIcon,
        color: config.templateColor,
        templateKey: config.templateKey,
        templateName: config.templateName,
        actions: [proposalAction],
      } satisfies BeginActionWorkflowSection,
    ];
  }

  const nextSections = [...workflowSections];
  const targetSection = nextSections[sectionIndex];
  const hasProposalAction = (targetSection.actions || []).some((action) => (
    (action.itemTypes || []).some((itemType) => normalizeWorkflowToken(itemType) === 'proposal')
  ));

  if (hasProposalAction) {
    return workflowSections;
  }

  nextSections[sectionIndex] = {
    ...targetSection,
    actions: dedupeBeginActionWorkflowSectionActions([
      ...(targetSection.actions || []),
      proposalAction,
    ]),
  };

  return nextSections;
}

function ensureServiceOperationsAppointmentCreateAction(
  workflowSections: BeginActionWorkflowSection[],
  config: SmartTaskConfig,
  canCreateItemType: CanCreateItemType,
): BeginActionWorkflowSection[] {
  if (!isServiceOperationsTemplate(config.templateKey)) {
    return workflowSections;
  }

  const canCreateAppointment = canCreateItemType('appointment');

  // Check if we already have appointment actions
  const hasAppointmentAction = workflowSections.some((section) => (
    (section.actions || []).some((action) => (
      (action.mode === 'create' || action.mode === 'select_existing')
      && (action.itemTypes || []).some((itemType) => normalizeWorkflowToken(itemType) === 'appointment')
    ))
  ));

  if (hasAppointmentAction) {
    return workflowSections;
  }

  // Always add "Appointment" action to view existing appointments
  const appointmentAction = buildCreateWorkflowSectionAction({
    key: 'appointment',
    label: 'Appointment',
    icon: 'Calendar',
    color: '#3b82f6',
    itemType: 'appointment',
    description: 'View and manage your assigned appointments.',
    mode: 'select_existing',
    pickerMode: 'appointment',
    action: 'open_appointment_picker',
  });
  // Add picker-specific labels
  appointmentAction.startLabel = 'Select Appointment';
  appointmentAction.emptyMessage = 'No appointments assigned to you';

  // Add "Create Appointment" action only if user has create permission
  const createAppointmentAction = canCreateAppointment
    ? buildCreateWorkflowSectionAction({
      key: 'create_appointment',
      label: 'Create Appointment',
      icon: 'CalendarPlus',
      color: '#7c3aed',
      itemType: 'appointment',
      description: 'Schedule a new appointment linked to a job for yourself.',
    })
    : null;

  const actions = createAppointmentAction
    ? [appointmentAction, createAppointmentAction]
    : [appointmentAction];

  const sectionIndex = workflowSections.findIndex((section) => (
    isServiceOperationsTemplate(section.templateKey || config.templateKey)
  ));

  if (sectionIndex === -1) {
    return [
      ...workflowSections,
      {
        key: 'service-operations',
        title: DEFAULT_WORKFLOW_SECTION_TITLES['service-operations'],
        description: DEFAULT_WORKFLOW_SECTION_DESCRIPTIONS['service-operations'],
        icon: config.templateIcon,
        color: config.templateColor,
        templateKey: config.templateKey,
        templateName: config.templateName,
        actions,
      } satisfies BeginActionWorkflowSection,
    ];
  }

  const nextSections = [...workflowSections];
  const targetSection = nextSections[sectionIndex];

  nextSections[sectionIndex] = {
    ...targetSection,
    actions: dedupeBeginActionWorkflowSectionActions([
      ...(targetSection.actions || []),
      ...actions,
    ]),
  };

  return nextSections;
}

function stripWorkflowChildEntryActions(
  workflowSections: BeginActionWorkflowSection[],
  childEntryTypes: ChildEntryType[],
  allowedTemplateKey: string,
): BeginActionWorkflowSection[] {
  const blockedChildEntryTypeSet = new Set(childEntryTypes);
  const normalizedAllowedTemplateKey = normalizeWorkflowToken(allowedTemplateKey);

  return workflowSections.map((section) => {
    const normalizedTemplateKey = normalizeWorkflowToken(section.templateKey || section.key);
    if (normalizedTemplateKey === normalizedAllowedTemplateKey) {
      return section;
    }

    return {
      ...section,
      actions: (section.actions || []).filter((action) => !action.childEntryType || !blockedChildEntryTypeSet.has(action.childEntryType)),
    };
  });
}

function ensureFinancialManagementExpenseActions(
  workflowSections: BeginActionWorkflowSection[],
  config: SmartTaskConfig,
): BeginActionWorkflowSection[] {
  const nextSections = stripWorkflowChildEntryActions(workflowSections, ['purchase'], 'financial-management');

  // If a capture_expense action already exists (from template DB), don't add another
  const alreadyHasCaptureExpense = nextSections.some((section) =>
    (section.actions || []).some((action) => action.key === 'capture_expense'),
  );

  if (alreadyHasCaptureExpense) {
    return nextSections;
  }

  const captureExpenseAction = config.features.purchaseRecording
    ? buildActionWorkflowSectionAction({
      key: 'capture_expense',
      label: 'Capture Expense',
      icon: 'Receipt',
      color: '#3b82f6',
      action: 'capture_expense',
      description: 'Capture any type of expense or procurement document.',
    })
    : null;

  if (!captureExpenseAction) {
    return nextSections;
  }

  const sectionIndex = nextSections.findIndex((section) => (
    isFinancialManagementTemplate(section.templateKey || section.key)
  ));

  if (sectionIndex === -1) {
    return [
      ...nextSections,
      {
        key: 'financial-management',
        title: DEFAULT_WORKFLOW_SECTION_TITLES['financial-management'],
        description: DEFAULT_WORKFLOW_SECTION_DESCRIPTIONS['financial-management'],
        icon: config.templateIcon,
        color: config.templateColor,
        templateKey: 'financial-management',
        templateName: 'Sales Management',
        actions: [captureExpenseAction],
      } satisfies BeginActionWorkflowSection,
    ];
  }

  const updatedSections = [...nextSections];
  const targetSection = updatedSections[sectionIndex];
  updatedSections[sectionIndex] = {
    ...targetSection,
    actions: dedupeBeginActionWorkflowSectionActions([
      ...(targetSection.actions || []),
      captureExpenseAction,
    ]),
  };

  return updatedSections;
}

function ensureFleetManagementExpenseActions(
  workflowSections: BeginActionWorkflowSection[],
  config: SmartTaskConfig,
): BeginActionWorkflowSection[] {
  const nextSections = stripWorkflowChildEntryActions(workflowSections, ['fuel', 'toll', 'maintenance'], 'fleet-management');

  // If a capture_expense action already exists (from template DB), don't add another
  const alreadyHasCaptureExpense = nextSections.some((section) =>
    (section.actions || []).some((action) => action.key === 'capture_expense'),
  );

  const fleetWorkflowActions = [
    !config.rules.isNonDriver
      ? buildActionWorkflowSectionAction({
        key: 'fleet_vehicle_listing',
        label: 'Vehicles',
        icon: 'Truck',
        color: '#f59e0b',
        action: 'select_vehicle',
        description: 'Open the vehicle listing and change the current allocation when needed.',
      })
      : null,
    config.features.purchaseRecording && !alreadyHasCaptureExpense
      ? buildActionWorkflowSectionAction({
        key: 'capture_expense',
        label: 'Capture Expense',
        icon: 'Receipt',
        color: '#3b82f6',
        action: 'capture_expense',
        description: 'Capture any type of expense or procurement document.',
        // Non-drivers never hold a vehicle allocation — requiring one would
        // permanently disable expense capture for them.
        requiresVehicle: !config.rules.isNonDriver,
      })
      : null,
  ].filter(isDefined);

  if (fleetWorkflowActions.length === 0) {
    return nextSections;
  }

  const sectionIndex = nextSections.findIndex((section) => (
    isFleetManagementTemplate(section.templateKey || section.key)
  ));

  if (sectionIndex === -1) {
    return [
      ...nextSections,
      {
        key: 'fleet-management',
        title: DEFAULT_WORKFLOW_SECTION_TITLES['fleet-management'],
        description: DEFAULT_WORKFLOW_SECTION_DESCRIPTIONS['fleet-management'],
        icon: config.templateIcon,
        color: config.templateColor,
        templateKey: 'fleet-management',
        templateName: 'Fleet Management',
        actions: fleetWorkflowActions,
      } satisfies BeginActionWorkflowSection,
    ];
  }

  const updatedSections = [...nextSections];
  const targetSection = updatedSections[sectionIndex];
  updatedSections[sectionIndex] = {
    ...targetSection,
    actions: dedupeBeginActionWorkflowSectionActions([
      ...(targetSection.actions || []),
      ...fleetWorkflowActions,
    ]),
  };

  return updatedSections;
}

function applyDefaultWorkflowSections(config: SmartTaskConfig, canCreateItemType: CanCreateItemType): SmartTaskConfig {
  const decorateWorkflowSection = (section: BeginActionWorkflowSection): BeginActionWorkflowSection => ({
    ...section,
    icon: section.icon || config.templateIcon,
    color: section.color || config.templateColor,
    templateKey: section.templateKey ?? config.templateKey,
    templateName: section.templateName ?? config.templateName,
    actions: dedupeBeginActionWorkflowSectionActions(section.actions || []),
  });

  const rawExisting = config.beginAction.workflowSections || [];
  const normalizedExisting = rawExisting.map(decorateWorkflowSection);
  const builtManufacturing = shouldUseAdvancedManufacturingWorkflowDefaults(config.templateKey, config.allowedItemTypes)
    ? buildAdvancedManufacturingWorkflowSections(config, canCreateItemType).map(decorateWorkflowSection)
    : [];
  const hasManufacturingLayout = normalizedExisting.some(beginActionSectionLooksManufacturing);

  let workflowSections: BeginActionWorkflowSection[];

  if (builtManufacturing.length > 0 && !hasManufacturingLayout) {
    // Client-level settings or another template layer can inject non-manufacturing `workflow_sections`
    // onto advanced-manufacturing (see `toResolvedTemplateConfig` merging clientSettings). Those
    // sections must not block the manufacturing begin-action layout (BeginActionModal / grid).
    const manufacturingKeys = new Set(builtManufacturing.map((section) => section.key));
    workflowSections = [...builtManufacturing, ...normalizedExisting.filter((section) => !manufacturingKeys.has(section.key))];
  } else if (normalizedExisting.length > 0) {
    workflowSections = normalizedExisting;
  } else {
    workflowSections = buildDefaultWorkflowSections(config, canCreateItemType);
  }

  // Non-drivers are not fleet members — strip any fleet-management workflow
  // section (vehicle listing, inspections) regardless of how it was injected
  // (DB template or client settings). ensureFleetManagementExpenseActions
  // below re-adds the section with only capture_expense when appropriate.
  if (config.rules.isNonDriver) {
    workflowSections = workflowSections.filter((section) => (
      !isFleetManagementTemplate(section.templateKey || section.key)
    ));
  }

  workflowSections = ensureFinancialManagementProposalAction(workflowSections, config, canCreateItemType);
  workflowSections = ensureServiceOperationsAppointmentCreateAction(workflowSections, config, canCreateItemType);
  workflowSections = ensureFinancialManagementExpenseActions(workflowSections, config);
  workflowSections = ensureFleetManagementExpenseActions(workflowSections, config);

  return {
    ...config,
    beginAction: {
      ...config.beginAction,
      workflowSections: workflowSections.length > 0
        ? Array.from(new Map(workflowSections.map((section) => [section.key, section])).values())
        : undefined,
    },
  };
}

function stripWorkflowSectionsForTemplate(
  template: SmartTaskConfig,
  blockedTemplateKey: string,
): SmartTaskConfig {
  const normalizedBlockedTemplateKey = normalizeWorkflowToken(blockedTemplateKey);
  const workflowSections = template.beginAction.workflowSections || [];
  const nextWorkflowSections = workflowSections.filter((section) => {
    const normalizedSectionTemplateKey = normalizeWorkflowToken(section.templateKey || section.key);
    return normalizedSectionTemplateKey !== normalizedBlockedTemplateKey;
  });

  if (nextWorkflowSections.length === workflowSections.length) {
    return template;
  }

  return {
    ...template,
    beginAction: {
      ...template.beginAction,
      workflowSections: nextWorkflowSections.length > 0 ? nextWorkflowSections : undefined,
    },
  };
}

function normalizeResolvedTemplatesForMultiTemplateConfig(
  resolvedTemplates: SmartTaskConfig[],
): SmartTaskConfig[] {
  const hasDedicatedFinancialManagementTemplate = resolvedTemplates.some((template) => (
    isFinancialManagementTemplate(template.templateKey)
  ));
  const hasDedicatedFleetManagementTemplate = resolvedTemplates.some((template) => (
    isFleetManagementTemplate(template.templateKey)
  ));
  const seenTemplateKeys = new Set<string>();

  return resolvedTemplates.reduce<SmartTaskConfig[]>((acc, template, index) => {
    const normalizedTemplateKey = normalizeWorkflowToken(template.templateKey || template.templateName || `template-${index}`);
    if (seenTemplateKeys.has(normalizedTemplateKey)) {
      return acc;
    }

    seenTemplateKeys.add(normalizedTemplateKey);

    let sanitizedTemplate = template;

    if (hasDedicatedFinancialManagementTemplate && !isFinancialManagementTemplate(template.templateKey)) {
      sanitizedTemplate = stripWorkflowSectionsForTemplate(sanitizedTemplate, 'financial-management');
    }

    if (hasDedicatedFleetManagementTemplate && !isFleetManagementTemplate(template.templateKey)) {
      sanitizedTemplate = stripWorkflowSectionsForTemplate(sanitizedTemplate, 'fleet-management');
    }

    acc.push(sanitizedTemplate);
    return acc;
  }, []);
}

// ============================================================================
// RESOLVER
// ============================================================================

/**
 * Resolve final SmartTask config from all layers.
 * 
 * Resolution chain (priority: lowest → highest):
 * DEFAULT_RULES → SOLUTION_DEFAULTS[solutionKey] → templateConfig → userFeatures
 * 
 * @param templateConfig - From API: operational_workflow_templates.config.smart_task
 * @param userFeatures - From API: module_access.custom_permissions (IAM store)
 * @param industrySolution - From API: client's active industry_solution
 */
export function resolveConfig(
  templateConfig: UnknownRecord | Array<UnknownRecord> | null,
  userFeatures: UnknownRecord | null,
  industrySolution: { solution_key: string; name: string; item_types: string[] } | null,
  resolvedVia?: Record<string, string> | null
): SmartTaskConfig {
  const startedAt = getSmartTaskColdStartNow();
  const templateConfigs = Array.isArray(templateConfig)
    ? templateConfig.filter(Boolean)
    : templateConfig ? [templateConfig] : [];

  writeSmartTaskColdStartLog('CONFIG_RESOLVE_START', 'Resolving Smart Task config', {
    templateCount: templateConfigs.length,
    hasUserFeatures: Boolean(userFeatures),
    solutionKey: industrySolution?.solution_key || undefined,
  });

  if (templateConfigs.length > 1) {
    const resolvedTemplates = normalizeResolvedTemplatesForMultiTemplateConfig(
      templateConfigs.map((config) => resolveSingleConfig(config, userFeatures, industrySolution, resolvedVia))
    );
    const primaryTemplate = resolvedTemplates[0];
    const templateKeys = resolvedTemplates
      .map((template) => template.templateKey)
      .filter((templateKey): templateKey is string => typeof templateKey === 'string' && templateKey.length > 0);
    const aggregatedWorkflowSections = Array.from(
      new Map(
        resolvedTemplates
          .flatMap((template) => (template.beginAction.workflowSections || []).map((section) => ({
            ...section,
            icon: section.icon || template.templateIcon,
            color: section.color || template.templateColor,
            templateKey: section.templateKey ?? template.templateKey,
            templateName: section.templateName ?? template.templateName,
            actions: dedupeBeginActionWorkflowSectionActions(section.actions || []),
          })))
          .map((section) => [`${section.templateKey || section.key}:${section.key}`, section])
      ).values()
    );

    const resolvedConfig = {
      ...primaryTemplate,
      beginAction: {
        ...primaryTemplate.beginAction,
        workflowSections: aggregatedWorkflowSections.length > 0
          ? aggregatedWorkflowSections
          : primaryTemplate.beginAction.workflowSections,
      },
      templateKeys,
      templateName: 'Multiple Workflows',
      documentTypes: Array.from(new Set(resolvedTemplates.flatMap((template) => template.documentTypes || []))),
      allowedItemTypes: Array.from(new Set(resolvedTemplates.flatMap((template) => template.allowedItemTypes || []))),
      queryItemTypes: Array.from(new Set(resolvedTemplates.flatMap((template) => template.queryItemTypes || template.allowedItemTypes || []))),
      quickActions: Array.from(
        new Map(
          resolvedTemplates
            .flatMap((template) => template.quickActions || [])
            .map((action) => [action.key, action])
        ).values()
      ),
      workflowTemplates: resolvedTemplates.map((template) => ({
        templateKey: template.templateKey,
        solutionKey: template.solutionKey ?? null,
        templateName: template.templateName,
        templateIcon: template.templateIcon,
        templateColor: template.templateColor,
        beginAction: template.beginAction,
        detailProfile: template.detailProfile,
        detailLayout: template.detailLayout,
        displayMode: template.displayMode,
        terminology: template.terminology,
        rules: template.rules,
        features: template.features,
        quickActions: template.quickActions,
        documentTypes: template.documentTypes,
        allowedItemTypes: template.allowedItemTypes,
        queryItemTypes: template.queryItemTypes,
        workflowHierarchy: template.workflowHierarchy,
        workflowStatuses: template.workflowStatuses,
        taskActions: template.taskActions,
        jobActions: template.jobActions,
        stopActions: template.stopActions,
        visitActions: template.visitActions,
        patrolActions: template.patrolActions,
        workOrderActions: template.workOrderActions,
        stageActions: template.stageActions,
        blockActions: template.blockActions,
        mobileOnly: template.mobileOnly,
        enforceGpsForSmartTask: template.enforceGpsForSmartTask,
        showClosedItems: template.showClosedItems,
        closedItemRetentionDays: template.closedItemRetentionDays,
      })),
    };

    writeSmartTaskColdStartDuration('CONFIG_RESOLVE_READY', 'Resolved multi-template Smart Task config', startedAt, {
      templateCount: templateConfigs.length,
      workflowSectionCount: aggregatedWorkflowSections.length,
      allowedItemTypes: resolvedConfig.allowedItemTypes.length,
    });
    return resolvedConfig;
  }

  const resolvedConfig = resolveSingleConfig(templateConfigs[0] || null, userFeatures, industrySolution, resolvedVia);
  writeSmartTaskColdStartDuration('CONFIG_RESOLVE_READY', 'Resolved single-template Smart Task config', startedAt, {
    templateCount: templateConfigs.length,
    templateKey: resolvedConfig.templateKey || undefined,
    allowedItemTypes: resolvedConfig.allowedItemTypes.length,
  });
  return resolvedConfig;
}

function resolveSingleConfig(
  templateConfig: UnknownRecord | null,
  userFeatures: UnknownRecord | null,
  industrySolution: { solution_key: string; name: string; item_types: string[] } | null,
  resolvedVia?: Record<string, string> | null
): SmartTaskConfig {
  const tc = templateConfig || {};
  // Merge solution defaults into user features as base layer
  const solutionDefaults = industrySolution?.solution_key ? SOLUTION_DEFAULTS[industrySolution.solution_key] : null;
  const sdFeatures = solutionDefaults?.field_worker_features || {};
  const uf = { ...sdFeatures, ...(userFeatures || {}) };
  const userRole = typeof uf.operations_role === 'string' && uf.operations_role.trim().length > 0
    ? uf.operations_role.trim()
    : 'user';
  const smartTaskTc = tc.smart_task || tc; // Template may nest under smart_task key
  // The template's workflow key scopes operations_action_permissions lookups
  // to this workflow's own Actions-tab matrix slice — other workflows' cells
  // (e.g. compliance-manager's appointment.create) must not leak in.
  const resolvedTemplateKey = smartTaskTc.template_key || tc.template_key || uf.smart_task_template_key || null;
  const canCreateItemType: CanCreateItemType = (itemType) => getEffectiveOperationsActionPermission(uf, userRole, itemType, 'create', resolvedTemplateKey);
  const legacyAppointmentSharingRules = (smartTaskTc.grouped_appointment_rules && typeof smartTaskTc.grouped_appointment_rules === 'object' && !Array.isArray(smartTaskTc.grouped_appointment_rules))
    ? smartTaskTc.grouped_appointment_rules as UnknownRecord
    : (smartTaskTc.groupedAppointmentRules && typeof smartTaskTc.groupedAppointmentRules === 'object' && !Array.isArray(smartTaskTc.groupedAppointmentRules))
      ? smartTaskTc.groupedAppointmentRules as UnknownRecord
      : {};
  const templateActionSignals = collectTemplateActionSignals(smartTaskTc);
  const inferredFeatureFlags = inferFeatureFlagsFromTemplateSignals(templateActionSignals, smartTaskTc);

  // Resolve terminology
  const terminology: TerminologyConfig = {
    ...DEFAULT_TERMINOLOGY,
    ...(smartTaskTc.terminology || {}),
  };

  // Resolve appointment-sharing rules. Legacy GPA/grouped-appointment config keys are
  // still accepted here, but the runtime rule names below follow the current model.
  const viewSiblingAppointments = uf.smart_task_gpa_view_other_appointments ?? legacyAppointmentSharingRules.view_other_appointments ?? legacyAppointmentSharingRules.viewOtherAppointments ?? smartTaskTc.gpa_view_other_appointments ?? DEFAULT_RULES.viewSiblingAppointments;
  const editSiblingAppointments = uf.smart_task_gpa_edit_other_appointments ?? legacyAppointmentSharingRules.edit_other_appointments ?? legacyAppointmentSharingRules.editOtherAppointments ?? smartTaskTc.gpa_edit_other_appointments ?? DEFAULT_RULES.editSiblingAppointments;
  const viewParentJobFromAppointment = uf.smart_task_gpa_view_parent_job ?? legacyAppointmentSharingRules.view_parent_job ?? legacyAppointmentSharingRules.viewParentJob ?? smartTaskTc.gpa_view_parent_job ?? DEFAULT_RULES.viewParentJobFromAppointment;
  const downloadAssignedAppointmentPhotos = uf.smart_task_gpa_download_group_photos ?? legacyAppointmentSharingRules.download_group_photos ?? legacyAppointmentSharingRules.downloadGroupPhotos ?? smartTaskTc.gpa_download_group_photos ?? DEFAULT_RULES.downloadAssignedAppointmentPhotos;
  const downloadParentJobPhotosFromAppointment = uf.smart_task_gpa_download_job_photos ?? legacyAppointmentSharingRules.download_parent_job_photos ?? legacyAppointmentSharingRules.downloadParentJobPhotos ?? smartTaskTc.gpa_download_parent_job_photos ?? DEFAULT_RULES.downloadParentJobPhotosFromAppointment;

  // Fleet settings (from FleetSettingsPanel / client_config) take priority over templates
  const fleetSettings = smartTaskTc.fleet_settings || {};

  // Explicit IAM values win over legacy client_config.fleet_settings. A key
  // counts as explicitly set only when a group policy or per-user override
  // produced it — definition defaults and seeded role defaults fall through
  // to fleet settings so FleetSettingsPanel keeps working for deployments
  // that never configured the IAM toggle.
  const iamExplicit = (key: string) => {
    const via = resolvedVia?.[key];
    return via === 'group_policy' || via === 'override';
  };
  const fleetOr = (iamKey: string, fleetValue: unknown) =>
    iamExplicit(iamKey) ? uf[iamKey] : (fleetValue ?? uf[iamKey]);

  const rules: OperationalRules = {
    ...DEFAULT_RULES,
    // Template overrides
    gpsEnforcement: smartTaskTc.gps_enforcement || smartTaskTc.gpsEnforcement || DEFAULT_RULES.gpsEnforcement,
    requireGPSForTravel: smartTaskTc.require_gps_for_travel ?? uf.require_gps_for_travel ?? DEFAULT_RULES.requireGPSForTravel,
    requireGeofenceForCheckin: smartTaskTc.require_geofence ?? DEFAULT_RULES.requireGeofenceForCheckin,
    geofenceRadiusMeters: smartTaskTc.geofence_radius_meters ?? DEFAULT_RULES.geofenceRadiusMeters,
    requireTravel: fleetSettings.require_job_before_travel ?? smartTaskTc.require_travel ?? DEFAULT_RULES.requireTravel,
    allowSkipTravel: smartTaskTc.allow_skip_travel ?? DEFAULT_RULES.allowSkipTravel,
    requireVehicle: fleetOr('require_vehicle_for_travel', fleetSettings.require_driver_assignment) ?? smartTaskTc.require_vehicle ?? DEFAULT_RULES.requireVehicle,
    requireVehicleProximity: uf.require_vehicle_proximity ?? smartTaskTc.require_vehicle_proximity ?? DEFAULT_RULES.requireVehicleProximity,
    vehicleProximityRadiusMeters: uf.vehicle_proximity_radius_meters ?? smartTaskTc.vehicle_proximity_radius_meters ?? DEFAULT_RULES.vehicleProximityRadiusMeters,
    requireOdometerReading: fleetSettings.require_odometer_at_start ?? fleetSettings.require_odometer_at_end ?? smartTaskTc.require_odometer ?? DEFAULT_RULES.requireOdometerReading,
    requireOdometerPhoto: fleetOr('require_odometer_photo', fleetSettings.inspection_photo_required) ?? smartTaskTc.require_odometer_photo ?? DEFAULT_RULES.requireOdometerPhoto,
    requireETAForTravel: uf.require_eta_for_travel ?? smartTaskTc.require_eta ?? DEFAULT_RULES.requireETAForTravel,
    requireDigitalLogbook: uf.require_digital_logbook ?? DEFAULT_RULES.requireDigitalLogbook,
    // Inspection conditions resolve ONLY from the IAM surface (solution
    // parent group policies / per-user overrides) — the industry solution is
    // the SSOT; client_config.fleet_settings no longer competes.
    requireDailyPreTripInspection: uf.require_daily_pre_trip_inspection ?? DEFAULT_RULES.requireDailyPreTripInspection,
    requireTakeOnInspection: uf.require_take_on_inspection ?? DEFAULT_RULES.requireTakeOnInspection,
    requireHandoverInspection: uf.require_handover_inspection ?? DEFAULT_RULES.requireHandoverInspection,
    requireAdminPresentForInspection: uf.require_admin_present_for_inspection ?? DEFAULT_RULES.requireAdminPresentForInspection,
    requireRandomInspection: uf.require_random_inspection ?? DEFAULT_RULES.requireRandomInspection,
    randomInspectionLeniencyDays: typeof uf.random_inspection_leniency_days === 'number'
      ? uf.random_inspection_leniency_days
      : DEFAULT_RULES.randomInspectionLeniencyDays,
    allowInspectionRemindLater: uf.allow_inspection_remind_later ?? DEFAULT_RULES.allowInspectionRemindLater,
    scheduledInspectionInterval: normalizeScheduledInspectionInterval(
      uf.scheduled_inspection_interval,
      uf.scheduled_inspection_frequency,
    ),
    requireInspectionAiAnpr: uf.require_inspection_ai_anpr ?? DEFAULT_RULES.requireInspectionAiAnpr,
    isNonDriver: uf.smart_task_non_driver ?? DEFAULT_RULES.isNonDriver,
    allowSkipVehicleRequirement: uf.smart_task_allow_skip_vehicle ?? DEFAULT_RULES.allowSkipVehicleRequirement,
    requirePhotoOnStart: uf.require_photos_before_complete ?? smartTaskTc.require_photo_on_start ?? DEFAULT_RULES.requirePhotoOnStart,
    requirePhotoOnComplete: uf.require_photos_after_complete ?? smartTaskTc.require_photo_on_complete ?? DEFAULT_RULES.requirePhotoOnComplete,
    requireSignatureOnComplete: uf.require_signature ?? smartTaskTc.require_signature ?? DEFAULT_RULES.requireSignatureOnComplete,
    requireArrivalPhoto: smartTaskTc.require_arrival_photo ?? DEFAULT_RULES.requireArrivalPhoto,
    requireChecklistComplete: smartTaskTc.require_checklist ?? DEFAULT_RULES.requireChecklistComplete,
    allowMultipleSignatures: uf.allow_multiple_signatures ?? DEFAULT_RULES.allowMultipleSignatures,
    requireClockInForSmartTask: uf.enable_smart_clock_integration ?? DEFAULT_RULES.requireClockInForSmartTask,
    autoPresentJobsOnClockIn: uf.auto_present_jobs_on_clock_in ?? DEFAULT_RULES.autoPresentJobsOnClockIn,
    autoCheckoutJobsOnClockOut: uf.auto_checkout_jobs_on_clock_out ?? DEFAULT_RULES.autoCheckoutJobsOnClockOut,
    enableGapAnalysis: uf.enable_gap_analysis ?? DEFAULT_RULES.enableGapAnalysis,
    enableDailyComplianceSummary: uf.enable_daily_compliance_summary ?? DEFAULT_RULES.enableDailyComplianceSummary,
    geofenceEnforcementMode: normalizeGeofenceEnforcementMode(
      uf.geofence_enforcement_mode
      ?? smartTaskTc.geofence_enforcement_mode
      ?? smartTaskTc.geofenceEnforcementMode
      ?? DEFAULT_RULES.geofenceEnforcementMode
    ),
    allowMultipleJobsWithoutCheckout: uf.allow_multiple_jobs_without_checkout ?? DEFAULT_RULES.allowMultipleJobsWithoutCheckout,
    allowMultipleSimultaneous: uf.allow_work_multiple_jobs_simultaneously ?? DEFAULT_RULES.allowMultipleSimultaneous,
    allowAppointmentDetours: uf.allow_appointment_detours ?? DEFAULT_RULES.allowAppointmentDetours,
    viewSiblingAppointments,
    editSiblingAppointments,
    viewParentJobFromAppointment,
    downloadAssignedAppointmentPhotos,
    downloadParentJobPhotosFromAppointment,
    // Legacy aliases retained for older callers and config consumers.
    enforceRouteOrder: uf.enforce_route_order ?? DEFAULT_RULES.enforceRouteOrder,
    generatePdfOnCompletion: uf.generate_pdf_on_completion ?? DEFAULT_RULES.generatePdfOnCompletion,
    generateInvoiceOnComplete: uf.generate_invoice_on_job_complete ?? DEFAULT_RULES.generateInvoiceOnComplete,
    generateQuoteOnComplete: uf.generate_quote_on_job_complete ?? DEFAULT_RULES.generateQuoteOnComplete,
    autoPopulateMaterials: uf.auto_populate_materials_on_job ?? DEFAULT_RULES.autoPopulateMaterials,
    autoPopulateExpenses: uf.auto_populate_expenses_on_job ?? DEFAULT_RULES.autoPopulateExpenses,
    autoPopulateTimeEntries: uf.auto_populate_time_entries_on_job ?? DEFAULT_RULES.autoPopulateTimeEntries,
    requirePartsBeforeComplete: uf.require_parts_before_complete ?? smartTaskTc.require_parts_before_complete ?? DEFAULT_RULES.requirePartsBeforeComplete,
    requireDocumentBeforeComplete: uf.require_document_before_complete ?? smartTaskTc.require_document_before_complete ?? DEFAULT_RULES.requireDocumentBeforeComplete,
    requireDataValidationComplete: uf.require_data_validation_complete ?? smartTaskTc.require_data_validation_complete ?? DEFAULT_RULES.requireDataValidationComplete,
  };

  // Resolve display mode (user > template > solution defaults > 'unified')
  const displayMode: DisplayMode = resolveSmartTaskDisplayMode(
    uf.smart_task_display_mode,
    smartTaskTc.display_mode,
    solutionDefaults?.display_mode,
  );

  // Resolve allowed item types (GUARD: JSONB may store as object instead of array)
  const rawItemTypes = smartTaskTc.allowed_item_types || tc.item_types || tc.item_type || industrySolution?.item_types || ['job', 'task'];
  const allowFinancialManagementProposalWorkflow = isFinancialManagementTemplate(resolvedTemplateKey)
    && (Boolean(uf.smart_task_proposal_creation) || canCreateItemType('proposal'));
  const allowedItemTypes: string[] = Array.isArray(rawItemTypes)
    ? rawItemTypes.filter((itemType): itemType is string => typeof itemType === 'string' && itemType.trim().length > 0)
    : typeof rawItemTypes === 'string' && rawItemTypes.trim().length > 0
      ? rawItemTypes.split(',').map((itemType) => itemType.trim()).filter(Boolean)
      : ['job', 'task'];
  const normalizedAllowedItemTypes = Array.from(new Set([
    ...allowedItemTypes.map((itemType) => normalizeWorkflowToken(itemType)).filter(Boolean),
    ...(allowFinancialManagementProposalWorkflow ? ['proposal'] : []),
  ]));

  // Resolve features - merge template + user features
  const features: FeatureFlags = {
    ...DEFAULT_FEATURES,
    travelTracking: smartTaskTc.travel_tracking ?? DEFAULT_FEATURES.travelTracking,
    childEntries: uf.smart_task_child_entries ?? smartTaskTc.child_entries ?? DEFAULT_FEATURES.childEntries,
    routes: uf.smart_task_routes ?? smartTaskTc.routes ?? smartTaskTc.enable_routes ?? inferredFeatureFlags.routes ?? DEFAULT_FEATURES.routes,
    appointments: uf.smart_task_appointments ?? getEffectiveOperationsActionPermission(uf, userRole, 'appointment', 'resolve') ?? smartTaskTc.appointments ?? DEFAULT_FEATURES.appointments,
    dailyForms: uf.smart_task_daily_forms ?? smartTaskTc.daily_forms ?? DEFAULT_FEATURES.dailyForms,
    smartClock: uf.enable_smart_clock_integration ?? DEFAULT_FEATURES.smartClock,
    jobMetrics: uf.smart_task_job_metrics ?? DEFAULT_FEATURES.jobMetrics,
    tollRecording: uf.smart_task_toll_recording ?? DEFAULT_FEATURES.tollRecording,
    fuelRecording: uf.smart_task_fuel_recording ?? DEFAULT_FEATURES.fuelRecording,
    breakdownRecording: uf.smart_task_breakdown_recording ?? DEFAULT_FEATURES.breakdownRecording,
    maintenanceRecording: uf.smart_task_maintenance_recording ?? DEFAULT_FEATURES.maintenanceRecording,
    siteVisitRecording: uf.smart_task_site_visit_recording ?? DEFAULT_FEATURES.siteVisitRecording,
    assessmentRecording: uf.smart_task_assessment_recording ?? DEFAULT_FEATURES.assessmentRecording,
    supervisionRecording: uf.smart_task_supervision_recording ?? DEFAULT_FEATURES.supervisionRecording,
    trainingRecording: uf.smart_task_training_recording ?? DEFAULT_FEATURES.trainingRecording,
    routeStopRecording: uf.smart_task_route_stop_recording ?? inferredFeatureFlags.routeStopRecording ?? DEFAULT_FEATURES.routeStopRecording,
    purchaseRecording: uf.smart_task_purchase_recording ?? smartTaskTc.features?.purchase_recording ?? smartTaskTc.features?.purchaseRecording ?? DEFAULT_FEATURES.purchaseRecording,
    procurementDocumentCapture: uf.procurement_document_capture ?? DEFAULT_FEATURES.procurementDocumentCapture,
    qrScanning: uf.enable_qr_scanning ?? smartTaskTc.features?.qr_scanning ?? smartTaskTc.features?.qrScanning ?? inferredFeatureFlags.qrScanning ?? DEFAULT_FEATURES.qrScanning,
    barcodeScanning: uf.enable_barcode_scanning ?? smartTaskTc.features?.barcode_scanning ?? DEFAULT_FEATURES.barcodeScanning,
    voiceNotes: uf.enable_voice_notes ?? smartTaskTc.features?.voice_notes ?? DEFAULT_FEATURES.voiceNotes,
    offlineMode: uf.enable_offline_mode ?? smartTaskTc.features?.offline_mode ?? DEFAULT_FEATURES.offlineMode,
    photoCapture: uf.enable_photo_capture ?? smartTaskTc.features?.photo_capture ?? inferredFeatureFlags.photoCapture ?? DEFAULT_FEATURES.photoCapture,
    signatureCapture: uf.enable_signature_capture ?? smartTaskTc.features?.signature_capture ?? inferredFeatureFlags.signatureCapture ?? DEFAULT_FEATURES.signatureCapture,
    checklistForms: uf.enable_checklist_forms ?? smartTaskTc.features?.checklist_forms ?? DEFAULT_FEATURES.checklistForms,
    locationTagging: uf.enable_location_tagging ?? smartTaskTc.features?.location_tagging ?? DEFAULT_FEATURES.locationTagging,
    timeTracking: uf.time_tracking_enabled ?? DEFAULT_FEATURES.timeTracking,
    partsLookup: uf.enable_parts_lookup ?? smartTaskTc.features?.parts_lookup ?? DEFAULT_FEATURES.partsLookup,
    customerHistory: uf.enable_customer_history ?? smartTaskTc.features?.customer_history ?? DEFAULT_FEATURES.customerHistory,
    navigation: uf.enable_navigation ?? smartTaskTc.features?.navigation ?? DEFAULT_FEATURES.navigation,
    podCapture: smartTaskTc.features?.pod_capture ?? DEFAULT_FEATURES.podCapture,
    drawingViewer: smartTaskTc.features?.drawing_viewer ?? DEFAULT_FEATURES.drawingViewer,
    bomLookup: smartTaskTc.features?.bom_lookup ?? DEFAULT_FEATURES.bomLookup,
    canCreateJobs: canCreateItemType('job'),
    canViewInventory: uf.can_view_inventory ?? DEFAULT_FEATURES.canViewInventory,
    viewAllJobs: uf.view_all_jobs ?? DEFAULT_FEATURES.viewAllJobs,
    viewAllOperations: uf.smart_task_view_all_operations ?? DEFAULT_FEATURES.viewAllOperations,
    viewJobFinancials: uf.view_job_financials ?? DEFAULT_FEATURES.viewJobFinancials,
    viewCustomerDetails: uf.view_customer_details ?? DEFAULT_FEATURES.viewCustomerDetails,
    canMarkEnRoute: uf.can_mark_job_en_route ?? DEFAULT_FEATURES.canMarkEnRoute,
    canMarkArrived: uf.can_mark_job_arrived ?? DEFAULT_FEATURES.canMarkArrived,
    canCompleteJobs: uf.can_complete_jobs ?? DEFAULT_FEATURES.canCompleteJobs,

    // Manufacturing / QA - from template config + user overrides
    enableNCRReporting: uf.enable_ncr_reporting ?? smartTaskTc.features?.ncr_reporting ?? smartTaskTc.features?.enableNCRReporting ?? DEFAULT_FEATURES.enableNCRReporting,
    enableBOMViewer: uf.enable_bom_viewer ?? smartTaskTc.features?.bom_viewer ?? smartTaskTc.features?.enableBOMViewer ?? DEFAULT_FEATURES.enableBOMViewer,
    enableDrawingViewer: uf.enable_drawing_viewer ?? smartTaskTc.features?.drawing_viewer ?? smartTaskTc.features?.enableDrawingViewer ?? DEFAULT_FEATURES.enableDrawingViewer,
    enableQCInspection: uf.enable_qc_inspection ?? smartTaskTc.features?.qc_inspection ?? smartTaskTc.features?.enableQCInspection ?? DEFAULT_FEATURES.enableQCInspection,
    enableStageManagement: uf.enable_stage_management ?? smartTaskTc.features?.stage_management ?? smartTaskTc.features?.enableStageManagement ?? DEFAULT_FEATURES.enableStageManagement,
    enableMeasurements: uf.enable_measurements ?? smartTaskTc.features?.measurements ?? smartTaskTc.features?.enableMeasurements ?? DEFAULT_FEATURES.enableMeasurements,
    enableComplianceIncidents: uf.enable_compliance_incidents ?? smartTaskTc.features?.compliance_incidents ?? smartTaskTc.features?.enableComplianceIncidents ?? DEFAULT_FEATURES.enableComplianceIncidents,
    enableComplianceAudits: uf.enable_compliance_audits ?? smartTaskTc.features?.compliance_audits ?? smartTaskTc.features?.enableComplianceAudits ?? DEFAULT_FEATURES.enableComplianceAudits,
    enableComplianceCertifications: uf.enable_compliance_certifications ?? smartTaskTc.features?.compliance_certifications ?? smartTaskTc.features?.enableComplianceCertifications ?? DEFAULT_FEATURES.enableComplianceCertifications,
    enableComplianceInspections: uf.enable_compliance_inspections ?? smartTaskTc.features?.compliance_inspections ?? smartTaskTc.features?.enableComplianceInspections ?? DEFAULT_FEATURES.enableComplianceInspections,
    enableComplianceHazards: uf.enable_compliance_hazards ?? smartTaskTc.features?.compliance_hazards ?? smartTaskTc.features?.enableComplianceHazards ?? DEFAULT_FEATURES.enableComplianceHazards,

    // Logistics - from template config + user overrides
    enablePackageScanning: uf.enable_package_scanning ?? smartTaskTc.features?.package_scanning ?? smartTaskTc.features?.enablePackageScanning ?? inferredFeatureFlags.enablePackageScanning ?? DEFAULT_FEATURES.enablePackageScanning,
    enableFailedDelivery: uf.enable_failed_delivery ?? smartTaskTc.features?.failed_delivery ?? smartTaskTc.features?.enableFailedDelivery ?? inferredFeatureFlags.enableFailedDelivery ?? DEFAULT_FEATURES.enableFailedDelivery,
    enableReportIssue: uf.enable_report_issue ?? smartTaskTc.features?.report_issue ?? smartTaskTc.features?.enableReportIssue ?? inferredFeatureFlags.enableReportIssue ?? DEFAULT_FEATURES.enableReportIssue,
    enableWaybillGeneration: uf.enable_waybill_generation ?? smartTaskTc.features?.waybill_generation ?? smartTaskTc.features?.enableWaybillGeneration ?? DEFAULT_FEATURES.enableWaybillGeneration,
    enableManifest: uf.enable_manifest ?? smartTaskTc.features?.manifest ?? smartTaskTc.features?.enableManifest ?? DEFAULT_FEATURES.enableManifest,
    enablePOD: uf.enable_pod ?? smartTaskTc.features?.pod ?? smartTaskTc.features?.enablePOD ?? DEFAULT_FEATURES.enablePOD,

    // Inventory / Asset Management - from template config + user overrides
    enableStockCount: uf.enable_stock_count ?? smartTaskTc.features?.stock_count ?? smartTaskTc.features?.enableStockCount ?? smartTaskTc.features?.enable_stock_count ?? inferredFeatureFlags.enableStockCount ?? DEFAULT_FEATURES.enableStockCount,

    // Field Service - from template config + user overrides
    enableTimeEntry: uf.enable_time_entry ?? smartTaskTc.features?.time_entry ?? smartTaskTc.features?.enableTimeEntry ?? inferredFeatureFlags.enableTimeEntry ?? DEFAULT_FEATURES.enableTimeEntry,
    enableQuoteGeneration: uf.enable_quote_generation ?? smartTaskTc.features?.quote_generation ?? smartTaskTc.features?.enableQuoteGeneration ?? inferredFeatureFlags.enableQuoteGeneration ?? DEFAULT_FEATURES.enableQuoteGeneration,
    enableFollowUpScheduling: uf.enable_follow_up_scheduling ?? smartTaskTc.features?.follow_up_scheduling ?? smartTaskTc.features?.enableFollowUpScheduling ?? inferredFeatureFlags.enableFollowUpScheduling ?? DEFAULT_FEATURES.enableFollowUpScheduling,
    enableSiteAssessment: uf.enable_site_assessment ?? smartTaskTc.features?.site_assessment ?? smartTaskTc.features?.enableSiteAssessment ?? DEFAULT_FEATURES.enableSiteAssessment,

    // Financial Management - from template config + user overrides
    proposalCreation: canCreateItemType('proposal'),
    quoteCreation: canCreateItemType('quote'),
    invoiceCreation: canCreateItemType('invoice'),
    expenseCreation: canCreateItemType('expense'),
    creditNoteCreation: canCreateItemType('credit_note'),

    // Fleet - from template config + user overrides
    enableVehicleChange: uf.enable_vehicle_change ?? smartTaskTc.features?.vehicle_change ?? smartTaskTc.features?.enableVehicleChange ?? DEFAULT_FEATURES.enableVehicleChange,
    enableOdometerCapture: uf.enable_odometer_capture ?? smartTaskTc.features?.odometer_capture ?? smartTaskTc.features?.enableOdometerCapture ?? DEFAULT_FEATURES.enableOdometerCapture,

    // Document Generation - from template config + user overrides
    enableDocumentGeneration: uf.enable_document_generation ?? smartTaskTc.features?.document_generation ?? smartTaskTc.features?.enableDocumentGeneration ?? inferredFeatureFlags.enableDocumentGeneration ?? DEFAULT_FEATURES.enableDocumentGeneration,
    enableServiceReports: uf.enable_service_reports ?? smartTaskTc.features?.service_reports ?? smartTaskTc.features?.enableServiceReports ?? DEFAULT_FEATURES.enableServiceReports,
  };

  // Apply generic snake_case → camelCase mapping for any permissions not
  // explicitly mapped above. This ensures all defined permissions from
  // permissionDefinitions.ts are available via config.features.<camelCaseKey>.
  applyGenericFeatureMappings(features as unknown as Record<string, unknown>, uf);

  // Resolve quick actions (GUARD: JSONB may store as object instead of array)
  const rawQuickActions = smartTaskTc.quick_actions || smartTaskTc.quickActions || DEFAULT_QUICK_ACTIONS;
  const quickActions: QuickAction[] = normalizeQuickActions(rawQuickActions, {
    displayMode,
    allowedItemTypes,
    requireTravel: rules.requireTravel,
  });

  // Apply display-mode-specific terminology overrides.
  // When a user selects appointment_centric mode, job/jobs should say "Appointment"/"Appointments"
  // regardless of baseline industry terminology.
  if (displayMode === 'appointment_centric') {
    terminology.job = terminology.appointment || 'Appointment';
    terminology.jobs = (terminology.appointment || 'Appointment') + 's';
  } else if (displayMode === 'task_centric') {
    terminology.job = terminology.task || 'Task';
    terminology.jobs = terminology.tasks || 'Tasks';
  }

  // Resolve document types (GUARD: JSONB may store as object instead of array)
  const rawDocTypes = smartTaskTc.document_types || ['job_card', 'service_report'];
  const documentTypes: string[] = Array.isArray(rawDocTypes) ? rawDocTypes : ['job_card', 'service_report'];

  const workflowHierarchy = normalizeWorkflowHierarchy(
    tc.workflow_hierarchy
    || smartTaskTc.workflow_hierarchy
    || tc.hierarchy
    || smartTaskTc.hierarchy
  );
  const workflowStatuses = normalizeWorkflowStatuses(
    tc.workflow_statuses
    || smartTaskTc.workflow_statuses
    || tc.statuses
    || smartTaskTc.statuses
  );

  const rawDetailProfile = typeof smartTaskTc.detail_profile === 'string'
    ? smartTaskTc.detail_profile.trim()
    : typeof smartTaskTc.detailProfile === 'string'
      ? smartTaskTc.detailProfile.trim()
      : '';
  const detailProfile = (DETAIL_PROFILE_KEYS as string[]).includes(rawDetailProfile)
    ? rawDetailProfile as DetailProfile
    : inferDetailProfile(resolvedTemplateKey, displayMode, normalizedAllowedItemTypes, features);
  const detailLayout = resolveDetailLayoutConfig(smartTaskTc, detailProfile, terminology);
  const beginAction = resolveBeginActionConfig(
    smartTaskTc,
    resolvedTemplateKey,
    features,
    rules,
    normalizedAllowedItemTypes,
    displayMode,
    quickActions,
    workflowHierarchy,
    workflowStatuses,
    canCreateItemType,
  );
  const queryItemTypes = Array.from(new Set([
    ...normalizedAllowedItemTypes,
    ...(beginAction.startActions || []).map((action) => action.itemType),
  ].map((itemType) => normalizeWorkflowToken(itemType)).filter(Boolean)));

  const resolvedConfig: SmartTaskConfig = {
    templateKey: resolvedTemplateKey,
    solutionKey: smartTaskTc.solution_key || tc.solution_key || null,
    templateKeys: Array.isArray(smartTaskTc.template_keys)
      ? smartTaskTc.template_keys.filter(Boolean)
      : Array.isArray(uf.smart_task_template_keys)
        ? uf.smart_task_template_keys.filter(Boolean)
        : undefined,
    templateName: smartTaskTc.template_name || tc.template_name || industrySolution?.name || 'Smart Task',
    templateIcon: smartTaskTc.icon || tc.icon || 'ClipboardList',
    templateColor: smartTaskTc.color || tc.color || '#3b82f6',
    userRole,
    beginAction,
    detailProfile,
    detailLayout,
    displayMode,
    terminology,
    rules,
    features,
    rawPermissions: uf,
    quickActions,
    documentTypes,
    allowedItemTypes: normalizedAllowedItemTypes,
    queryItemTypes,
    workflowHierarchy,
    workflowStatuses,
    vehicleChecks: smartTaskTc.vehicle_checks,
    floorZones: smartTaskTc.floor_zones || smartTaskTc.site_zones,
    deliveryTypes: smartTaskTc.delivery_types,

    // Industry-specific task/work type definitions
    taskTypes: smartTaskTc.task_types,
    serviceTypes: smartTaskTc.service_types,
    workOrderTypes: smartTaskTc.work_order_types,
    wasteTypes: smartTaskTc.waste_types,
    cleaningTypes: smartTaskTc.cleaning_types,
    patrolTypes: smartTaskTc.patrol_types,
    careTypes: smartTaskTc.care_types,
    visitTypes: smartTaskTc.visit_types,
    operationTypes: smartTaskTc.operation_types,
    containerTypes: smartTaskTc.container_types,
    areaTypes: smartTaskTc.area_types,
    incidentTypes: smartTaskTc.incident_types,
    assetCategories: smartTaskTc.asset_categories,

    // Action definitions per workflow state
    taskActions: smartTaskTc.task_actions,
    jobActions: smartTaskTc.job_actions,
    stopActions: smartTaskTc.stop_actions,
    visitActions: smartTaskTc.visit_actions,
    patrolActions: smartTaskTc.patrol_actions,
    workOrderActions: smartTaskTc.work_order_actions,
    stageActions: smartTaskTc.stage_actions,
    blockActions: smartTaskTc.block_actions,

    // Equipment, measurement, and sensor types
    equipmentTypes: smartTaskTc.equipment_types,
    measurementTypes: smartTaskTc.measurement_types,
    meterTypes: smartTaskTc.meter_types,
    vitalsTypes: smartTaskTc.vitals_types,
    qrCodeTypes: smartTaskTc.qr_code_types,

    // Industry-specific complex configs
    sprayApplication: smartTaskTc.spray_application,
    qualityInspection: smartTaskTc.quality_inspection,
    safetyRequirements: smartTaskTc.safety_requirements,
    checkpointConfig: smartTaskTc.checkpoint_config,
    siteZones: smartTaskTc.site_zones,
    inspectionAreas: smartTaskTc.inspection_areas,
    conditionRatings: smartTaskTc.condition_ratings,

    // Template-level behavior flags
    mobileOnly: uf.smart_task_mobile_only ?? false,
    enforceGpsForSmartTask: uf.enforce_gps_smart_task ?? false,
    showClosedItems: uf.smart_task_closed_item_retention !== false,
    closedItemRetentionDays: Number(uf.smart_task_closed_item_retention_days) || 7,
    autoNavigate: smartTaskTc.auto_navigate,
    continuousTracking: smartTaskTc.continuous_tracking,
    enableRoutes: smartTaskTc.enable_routes,
    privacyMode: smartTaskTc.privacy_mode,
    equipmentTracking: smartTaskTc.equipment_tracking,
    volumeTracking: smartTaskTc.volume_tracking,
  };

  return applyDefaultWorkflowSections(resolvedConfig, canCreateItemType);
}

/**
 * Get industry-specific terminology overrides
 */
export function getIndustryTerminology(solutionKey: string): Partial<TerminologyConfig> {
  const INDUSTRY_TERMS: Record<string, Partial<TerminologyConfig>> = {
    'logistics-transport': { job: 'Delivery', jobs: 'Deliveries', checkin: 'Arrive', checkout: 'Depart', travel: 'In Transit', complete: 'Delivered' },
    'security-services': { job: 'Patrol', jobs: 'Patrols', checkin: 'Start Patrol', checkout: 'End Patrol', travel: 'En Route', complete: 'Patrol Complete' },
    'cleaning-services': { job: 'Service', jobs: 'Services', checkin: 'Check In', checkout: 'Check Out', complete: 'Complete' },
    'field-services': { job: 'Service Call', jobs: 'Service Calls', checkin: 'Check In', checkout: 'Check Out', complete: 'Complete' },
    'waste-management-enterprise': { job: 'Collection', jobs: 'Collections', checkin: 'Start Collection', checkout: 'End Collection', travel: 'En Route', complete: 'Collected' },
    'healthcare-services': { job: 'Visit', jobs: 'Visits', checkin: 'Start Visit', checkout: 'End Visit', appointment: 'Appointment', complete: 'Visit Complete' },
    'construction-contracting': { job: 'Task', jobs: 'Tasks', checkin: 'Clock On', checkout: 'Clock Off', complete: 'Task Complete' },
    'advanced-manufacturing': { job: 'Work Order', jobs: 'Work Orders', checkin: 'Start Operation', checkout: 'End Operation', complete: 'Operation Complete' },
    'real-estate-management': { job: 'Work Order', jobs: 'Work Orders', appointment: 'Viewing', checkin: 'Arrive', complete: 'Completed' },
    'automotive-services': { job: 'Repair Order', jobs: 'Repair Orders', checkin: 'Start Repair', checkout: 'End Repair', complete: 'Repair Complete' },
    'hospitality-management': { job: 'Service Request', jobs: 'Service Requests', checkin: 'Check In', complete: 'Completed' },
    'forestry-operations': { job: 'Harvest Job', jobs: 'Harvest Jobs', checkin: 'Start Harvest', checkout: 'End Harvest', complete: 'Harvest Complete' },
    'agriculture-farming': { job: 'Field Task', jobs: 'Field Tasks', checkin: 'Start Task', checkout: 'End Task', complete: 'Task Complete' },
    'home-care': { job: 'Visit', jobs: 'Visits', checkin: 'Start Visit', checkout: 'End Visit', appointment: 'Visit', complete: 'Visit Complete' },
    'sales-rep': { job: 'Call', jobs: 'Calls', checkin: 'Check In', checkout: 'Check Out', travel: 'Traveling', complete: 'Call Complete', workItem: 'Visit', workItems: 'Visits' },
    'pest-control': { job: 'Treatment', jobs: 'Treatments', checkin: 'Start Treatment', checkout: 'End Treatment', complete: 'Treatment Complete' },
    'landscaping': { job: 'Service', jobs: 'Services', checkin: 'Check In', checkout: 'Check Out', complete: 'Complete' },
    'telecommunications': { job: 'Service Order', jobs: 'Service Orders', checkin: 'Start Work', checkout: 'End Work', complete: 'Order Complete' },
    'mining-operations': { job: 'Work Order', jobs: 'Work Orders', checkin: 'Start Shift', checkout: 'End Shift', complete: 'Shift Complete' },
    'energy-utilities': { job: 'Work Order', jobs: 'Work Orders', checkin: 'Start Work', checkout: 'End Work', complete: 'Order Complete' },
    'marine-maritime': { job: 'Voyage', jobs: 'Voyages', checkin: 'Embark', checkout: 'Disembark', complete: 'Voyage Complete' },
    'facility-asset-maintenance': { job: 'Work Order', jobs: 'Work Orders', checkin: 'Start Maintenance', checkout: 'End Maintenance', complete: 'Maintenance Complete' },
    'education-training': { job: 'Session', jobs: 'Sessions', checkin: 'Start Session', checkout: 'End Session', appointment: 'Class', complete: 'Session Complete' },
    'enterprise-project-management': { job: 'Task', jobs: 'Tasks', checkin: 'Start Task', checkout: 'End Task', complete: 'Task Complete', workItem: 'Deliverable', workItems: 'Deliverables' },
    'events-entertainment': { job: 'Event Task', jobs: 'Event Tasks', checkin: 'Check In', checkout: 'Check Out', complete: 'Complete', workItem: 'Event', workItems: 'Events' },
    'gis-solutions': { job: 'Survey', jobs: 'Surveys', checkin: 'Start Survey', checkout: 'End Survey', complete: 'Survey Complete', workItem: 'Survey Point', workItems: 'Survey Points' },
    'legal-services': { job: 'Matter', jobs: 'Matters', checkin: 'Start Work', checkout: 'End Work', appointment: 'Hearing', complete: 'Matter Closed', workItem: 'Case', workItems: 'Cases' },
    'nonprofit-management': { job: 'Activity', jobs: 'Activities', checkin: 'Check In', checkout: 'Check Out', complete: 'Complete', workItem: 'Program', workItems: 'Programs' },
    'project-management': { job: 'Task', jobs: 'Tasks', checkin: 'Start', checkout: 'Stop', complete: 'Done', workItem: 'Deliverable', workItems: 'Deliverables' },
    'qa-management': { job: 'Audit', jobs: 'Audits', checkin: 'Start Audit', checkout: 'End Audit', complete: 'Audit Complete', workItem: 'Finding', workItems: 'Findings' },
    'restaurant-food-service': { job: 'Order', jobs: 'Orders', checkin: 'Start Prep', checkout: 'End Prep', complete: 'Served', workItem: 'Table', workItems: 'Tables' },
    'retail-operations': { job: 'Task', jobs: 'Tasks', checkin: 'Clock In', checkout: 'Clock Out', complete: 'Complete', workItem: 'Store Visit', workItems: 'Store Visits' },
    'software-development': { job: 'Sprint Task', jobs: 'Sprint Tasks', checkin: 'Start Work', checkout: 'End Work', complete: 'Done', workItem: 'Story', workItems: 'Stories' },
    'workplace-management': { job: 'Work Order', jobs: 'Work Orders', checkin: 'Start Work', checkout: 'End Work', complete: 'Complete', workItem: 'Request', workItems: 'Requests' },
  };
  return INDUSTRY_TERMS[solutionKey] || {};
}
